import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from './hooks/useTheme';
import ProductGridView from './components/ProductGridView';
import FeaturedProductsCarousel from './components/FeaturedProductsCarousel';
import { 
  ArrowLeft, 
  Search,
  ChevronRight,
  X, 
  Plus, 
  Minus, 
  Trash2, 
  Home, 
  FileText, 
  Tag, 
  ShoppingCart,
  Truck,
  Store,
  ChevronDown,
  ChevronUp,
  Package,
  DollarSign,
  Clock,
  Star,
  CheckCircle,
  Copy,
  XCircle,
  Check
} from 'lucide-react';
import { Switch } from './components/ui/switch';
import PixCheckoutModal from './components/PixCheckoutModal';

// Detectar automaticamente a URL do backend baseado no hostname
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  
  // Se estiver em meucardapiozap.top, usar essa URL
  if (hostname === 'meucardapiozap.top' || hostname === 'www.meucardapiozap.top') {
    return 'https://meucardapiozap.top';
  }
  
  // Se estiver em preview do Emergent
  if (hostname.includes('.preview.emergentagent.com')) {
    return `https://${hostname}`;
  }
  
  // Se estiver em produção do Emergent (.emergent.host)
  if (hostname.includes('.emergent.host')) {
    // Força usar meucardapiozap.top em produção
    return 'https://meucardapiozap.top';
  }
  
  // Fallback para variável de ambiente ou localhost
  return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// ============================================================
// COMPONENTE: NeighborhoodSelector
// Seletor de bairros com busca e visual moderno
// ============================================================
const NeighborhoodSelector = ({ zones, selectedZone, onSelect, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);
  
  // Mostrar busca apenas se tiver 15+ bairros
  const showSearchField = zones.length >= 15;
  
  // Filtrar bairros pela busca (case-insensitive, sem acentos)
  const normalizeText = (text) => {
    return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };
  
  const filteredZones = useMemo(() => {
    if (!searchTerm.trim()) return zones;
    const normalized = normalizeText(searchTerm);
    return zones.filter(zone => normalizeText(zone.name).includes(normalized));
  }, [zones, searchTerm]);
  
  // Ordenar alfabeticamente
  const sortedZones = useMemo(() => {
    return [...filteredZones].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [filteredZones]);
  
  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // NÃO focar automaticamente no input - deixar usuário ver a lista primeiro
  // O usuário pode tocar no campo de busca se quiser digitar
  
  // Destacar texto que combina com a busca
  const highlightMatch = (text, search) => {
    if (!search.trim()) return text;
    const normalizedSearch = normalizeText(search);
    const normalizedText = normalizeText(text);
    const index = normalizedText.indexOf(normalizedSearch);
    
    if (index === -1) return text;
    
    const beforeMatch = text.slice(0, index);
    const match = text.slice(index, index + search.length);
    const afterMatch = text.slice(index + search.length);
    
    return (
      <>
        {beforeMatch}
        <span className="bg-yellow-200 text-yellow-900 font-semibold rounded px-0.5">{match}</span>
        {afterMatch}
      </>
    );
  };
  
  // Se não há bairros cadastrados
  if (zones.length === 0) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Bairro</label>
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-red-500 text-lg">⚠️</span>
          <div>
            <p className="text-sm font-medium text-red-700">Entrega indisponível</p>
            <p className="text-xs text-red-600">Este estabelecimento ainda não cadastrou bairros para entrega.</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">Bairro</label>
      
      {/* Botão de seleção */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-3 border rounded-xl text-left flex items-center justify-between transition-all ${
          error ? 'border-red-400 bg-red-50' : 
          selectedZone ? 'border-green-400 bg-green-50' : 
          'border-gray-200 bg-white hover:border-gray-300'
        }`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectedZone ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="font-medium text-gray-900 truncate">{selectedZone.name}</span>
              <span className="ml-auto flex-shrink-0 bg-gray-100 text-gray-700 text-sm font-medium px-2 py-0.5 rounded-full">
                R$ {selectedZone.fee.toFixed(2).replace('.', ',')}
              </span>
            </>
          ) : (
            <>
              <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-gray-500">Selecione seu bairro</span>
            </>
          )}
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Taxa de entrega quando selecionado */}
      {selectedZone && !error && !isOpen && (
        <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
          <Truck className="w-3 h-3" />
          Taxa de entrega: R$ {selectedZone.fee.toFixed(2).replace('.', ',')}
        </p>
      )}
      
      {/* Mensagem de erro */}
      {error && (
        <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          {error}
        </p>
      )}
      
      {/* Dropdown com lista de bairros */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Header com busca (somente se tiver 15+ bairros) */}
          <div className="sticky top-0 bg-white border-b border-gray-100 p-3">
            {showSearchField ? (
              <>
                {/* Barra de busca - NÃO foca automaticamente */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    inputMode="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Toque para buscar..."
                    className="w-full pl-9 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  )}
                </div>
                {/* Contador de resultados */}
                <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                  <span>
                    {searchTerm ? (
                      sortedZones.length === 0 ? 'Nenhum bairro encontrado' : 
                      `${sortedZones.length} bairro${sortedZones.length !== 1 ? 's' : ''} encontrado${sortedZones.length !== 1 ? 's' : ''}`
                    ) : (
                      `${zones.length} bairro${zones.length !== 1 ? 's' : ''} disponíve${zones.length !== 1 ? 'is' : 'l'}`
                    )}
                  </span>
                  {selectedZone && (
                    <button 
                      type="button"
                      onClick={() => { onSelect(null); setIsOpen(false); }}
                      className="text-red-500 hover:text-red-700 font-medium"
                    >
                      Limpar
                    </button>
                  )}
                </div>
              </>
            ) : (
              /* Se poucos bairros, mostrar apenas o contador e botão limpar */
              <div className="flex justify-between items-center text-xs text-gray-500">
                <span className="font-medium text-gray-700">
                  {zones.length} bairro{zones.length !== 1 ? 's' : ''} disponíve{zones.length !== 1 ? 'is' : 'l'}
                </span>
                {selectedZone && (
                  <button 
                    type="button"
                    onClick={() => { onSelect(null); setIsOpen(false); }}
                    className="text-red-500 hover:text-red-700 font-medium"
                  >
                    Limpar
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Lista de bairros */}
          <div className="max-h-64 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
            {sortedZones.length === 0 ? (
              <div className="p-6 text-center">
                <div className="text-gray-400 mb-2">
                  <Search className="w-8 h-8 mx-auto opacity-50" />
                </div>
                <p className="text-gray-500 text-sm">Nenhum bairro encontrado</p>
                <p className="text-gray-400 text-xs mt-1">Tente buscar por outro nome</p>
              </div>
            ) : (
              <div className="py-1">
                {sortedZones.map((zone, index) => {
                  const isSelected = selectedZone?.id === zone.id;
                  return (
                    <button
                      key={zone.id}
                      type="button"
                      onClick={() => {
                        onSelect(zone);
                        setIsOpen(false);
                        setSearchTerm('');
                      }}
                      className={`w-full px-4 py-3 flex items-center justify-between transition-all ${
                        isSelected 
                          ? 'bg-green-50 border-l-4 border-green-500' 
                          : 'hover:bg-gray-50 border-l-4 border-transparent'
                      } ${index !== sortedZones.length - 1 ? 'border-b border-gray-50' : ''}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {/* Indicador de seleção */}
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          isSelected 
                            ? 'border-green-500 bg-green-500' 
                            : 'border-gray-300'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        {/* Nome do bairro */}
                        <span className={`text-sm truncate ${isSelected ? 'font-semibold text-green-800' : 'text-gray-700'}`}>
                          {highlightMatch(zone.name, searchTerm)}
                        </span>
                      </div>
                      {/* Badge de preço */}
                      <span className={`flex-shrink-0 text-sm font-medium px-2.5 py-1 rounded-full ${
                        isSelected 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        R$ {zone.fee.toFixed(2).replace('.', ',')}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// COMPONENTE: ProductSkeleton
// Skeleton loader para produtos (mostra enquanto carrega)
// ============================================================
const ProductSkeleton = ({ layout = 'list' }) => {
  if (layout === 'grid') {
    return (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden animate-pulse">
        <div className="aspect-square bg-gray-200" />
        <div className="p-3 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-200 rounded w-1/2" />
          <div className="h-5 bg-gray-200 rounded w-1/3 mt-2" />
        </div>
      </div>
    );
  }
  
  // Layout list
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm flex gap-4 animate-pulse">
      <div className="flex-1 space-y-2">
        <div className="h-5 bg-gray-200 rounded w-3/4" />
        <div className="h-3 bg-gray-200 rounded w-full" />
        <div className="h-3 bg-gray-200 rounded w-2/3" />
        <div className="h-5 bg-gray-200 rounded w-1/4 mt-2" />
      </div>
      <div className="w-24 h-24 bg-gray-200 rounded-lg flex-shrink-0" />
    </div>
  );
};

// Skeleton para seção de categoria
const CategorySkeleton = () => (
  <div className="space-y-4 animate-pulse">
    <div className="h-6 bg-gray-200 rounded w-1/3" />
    <div className="space-y-3">
      {[1, 2, 3].map(i => (
        <ProductSkeleton key={i} />
      ))}
    </div>
  </div>
);

// ============================================================
// PLACEHOLDER: Imagem de garfo e faca cruzados (idêntica à referência do usuário)
// Imagem convertida para base64 - carrega instantaneamente (~1.7KB)
// ============================================================
const FOOD_PLACEHOLDER_IMG = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4QBMRXhpZgAATU0AKgAAAAgABAEAAAQAAAABAAAEOAEBAAQAAAABAAAEQAESAAMAAAABAAEAAIdpAAQAAAABAAAAPgAAAAAAAAAAAAD/4gIoSUNDX1BST0ZJTEUAAQEAAAIYAAAAAAQwAABtbnRyUkdCIFhZWiAAAAAAAAAAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAAHRyWFlaAAABZAAAABRnWFlaAAABeAAAABRiWFlaAAABjAAAABRyVFJDAAABoAAAAChnVFJDAAABoAAAAChiVFJDAAABoAAAACh3dHB0AAAByAAAABRjcHJ0AAAB3AAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAFgAAAAcAHMAUgBHAEIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAFhZWiAAAAAAAABvogAAOPUAAAOQWFlaIAAAAAAAAGKZAAC3hQAAGNpYWVogAAAAAAAAJKAAAA+EAAC2z3BhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABYWVogAAAAAAAA9tYAAQAAAADTLW1sdWMAAAAAAAAAAQAAAAxlblVTAAAAIAAAABwARwBvAG8AZwBsAGUAIABJAG4AYwAuACAAMgAwADEANv/bAEMADQkKCwoIDQsKCw4ODQ8TIBUTEhITJxweFyAuKTEwLiktLDM6Sj4zNkY3LC1AV0FGTE5SU1IyPlphWlBgSlFST//bAEMBDg4OExETJhUVJk81LTVPT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT//AABEIAGQAYwMBIgACEQEDEQH/xAAZAAEBAQEBAQAAAAAAAAAAAAAAAQQDAgb/xAApEAEAAgIABQMEAgMAAAAAAAAAAQIDEQQSITFBIlFhEzJCcVLhgaHB/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAH/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwD7BREVRFAEUAAARQBFBNCgAICg90pvrbtreo769weB2vj5utdc2t6jtaPeHARQBQa+Hwa1e8dfEezLad2tMdpkR5UgFBAFEAV0xW/GZ1EzuJ/jLmCNMRMzrUxO+sR+M+8fBnw+j6nTmj7tdpMGesRrJ0mI1Fvh2tMZcd647RM6UYGrh8GvXeOviHnhsUTaZv3rP2+zTe9cdea0/wBg8cRk5MU6726QwQ95Mk5Lza3+I9ngFARQQBUVAURQGjBhyRX6lZ1PiJ8rw/D79d46eIOKzd8dJ/c/8VHTpmrGTHPLeOn9Sx35+eeffNHfb1hyTjtuO3mGvJjrnpFqz18SDCkLas1tNbRqYRFURQBAFAAdeGrW2WItr4/bkA28Rm5I5az6p/0xAqDrw+b6dtT9s9/hyRFbeKik4ubcb8T7sSoIKAoIAoAAAAAIoAIoAAAAIACgAIoAIAAAKAAAD//Z";

// ============================================================
// COMPONENTE: LazyImage
// Imagem com placeholder + decode() + delay mínimo para eliminar flicker
// ============================================================
const LazyImage = React.memo(({ src, alt, className, ...props }) => {
  const [showReal, setShowReal] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(null);
  const containerRef = useRef(null);
  const prevSrcRef = useRef(src);
  
  const fallbackImage = "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=300&h=200&fit=crop&crop=center";
  
  // Reset se a URL mudar
  useEffect(() => {
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      setShowReal(false);
      setCurrentSrc(null);
    }
  }, [src]);
  
  // Intersection Observer para lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  // Carregar imagem em background com decode() + delay mínimo
  useEffect(() => {
    if (!isInView || !src) return;
    
    let cancelled = false;
    const startTime = Date.now();
    const MIN_DELAY = 260;
    
    const img = new Image();
    img.src = src;
    
    const handleLoad = async () => {
      try {
        // Aguardar decodificação completa
        if (img.decode) {
          await img.decode();
        }
        
        if (cancelled) return;
        
        // Garantir delay mínimo de 230ms
        const elapsed = Date.now() - startTime;
        const remaining = MIN_DELAY - elapsed;
        
        if (remaining > 0) {
          await new Promise(resolve => setTimeout(resolve, remaining));
        }
        
        if (cancelled) return;
        
        // Troca direta, sem transição
        setCurrentSrc(src);
        setShowReal(true);
      } catch {
        if (!cancelled) {
          setCurrentSrc(fallbackImage);
          setShowReal(true);
        }
      }
    };
    
    const handleError = () => {
      if (!cancelled) {
        setCurrentSrc(fallbackImage);
        setShowReal(true);
      }
    };
    
    img.onload = handleLoad;
    img.onerror = handleError;
    
    return () => {
      cancelled = true;
    };
  }, [isInView, src, fallbackImage]);
  
  return (
    <div ref={containerRef} className={`relative overflow-hidden bg-gray-100 ${className}`} {...props}>
      {/* Placeholder ou Imagem Real - troca direta, sem transição */}
      <img 
        src={showReal ? currentSrc : FOOD_PLACEHOLDER_IMG}
        alt={showReal ? alt : ""}
        aria-hidden={!showReal}
        className="w-full h-full object-cover"
      />
    </div>
  );
});

const PublicMenu = ({ subdomain = 'yurelanches' }) => {
  // 🎨 Load and apply theme with custom colors from backend
  const { theme, loading: themeLoading } = useTheme(subdomain);
  
  // 📐 Load layout preference from backend
  const [layout, setLayout] = useState('list');
  
  // 🔍 Search functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  
  const [currentScreen, setCurrentScreen] = useState('home');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cart, setCart] = useState([]);
  const [expandedSection, setExpandedSection] = useState(false);
  const [expandedImage, setExpandedImage] = useState(false); // Estado para imagem expandida
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [deliveryType, setDeliveryType] = useState('delivery');
  const [address, setAddress] = useState({
    cep: '',
    neighborhood: '',
    street: '',
    number: '',
    complement: '',
    city: '',
    state: ''
  });
  const [loadingCep, setLoadingCep] = useState(false); // Loading da busca de CEP
  const [paymentMethod, setPaymentMethod] = useState('dinheiro');
  const [needsChange, setNeedsChange] = useState(false);
  const [changeAmount, setChangeAmount] = useState('');
  const [pixCopied, setPixCopied] = useState(false);
  const [showCartConfirmation, setShowCartConfirmation] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [showStoreProfile, setShowStoreProfile] = useState(false);
  const [showClosedStoreModal, setShowClosedStoreModal] = useState(false);
  const [showMinOrderModal, setShowMinOrderModal] = useState(false); // Modal de valor mínimo
  const [showPixCheckout, setShowPixCheckout] = useState(false);
  const [pixOrderData, setPixOrderData] = useState(null);
  
  // 🎉 Payment Success Screen states
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [successOrderData, setSuccessOrderData] = useState(null);
  
  // ✅ Order Confirmation Screen states (nova tela de confirmação)
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [confirmationOrderData, setConfirmationOrderData] = useState(null);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  
  // 📅 Scheduling states (Agendamento)
  const [showSchedulingModal, setShowSchedulingModal] = useState(false);
  const [schedulingSlots, setSchedulingSlots] = useState([]);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState(null);
  const [selectedScheduleTime, setSelectedScheduleTime] = useState(null);
  const [isScheduledOrder, setIsScheduledOrder] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [schedulingModalShown, setSchedulingModalShown] = useState(false); // Controle para evitar re-exibição
  
  // Cashback states
  const [customerPhone, setCustomerPhone] = useState('');
  const [cashbackData, setCashbackData] = useState(null);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [useCashback, setUseCashback] = useState(false);
  const [cashbackAmount, setCashbackAmount] = useState(0);
  
  // 🎟️ Coupon states
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [showCouponSuccess, setShowCouponSuccess] = useState(false);
  const [hasActiveCoupons, setHasActiveCoupons] = useState(false);
  const [showCouponInput, setShowCouponInput] = useState(false);
  
  // Real data from backend
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);  // Produtos em destaque
  const [additionals, setAdditionals] = useState([]);
  const [additionalGroups, setAdditionalGroups] = useState([]);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Helper para calcular total dos adicionais considerando cobrar_mais_caro
  const calculateAdditionalsTotal = (additionalsObj, productAdditionalGroups = []) => {
    const additionalsList = Object.entries(additionalsObj).filter(([key, add]) => add && add.quantity > 0);
    
    // Agrupar por grupo
    const byGroup = {};
    additionalsList.forEach(([key, add]) => {
      const groupId = key.split('_')[0];
      if (!byGroup[groupId]) byGroup[groupId] = [];
      byGroup[groupId].push(add);
    });
    
    let total = 0;
    Object.entries(byGroup).forEach(([groupId, groupAdds]) => {
      const group = additionalGroups.find(g => g.id === groupId);
      if (group && group.cobrar_mais_caro) {
        // Cobrar apenas o mais caro
        total += Math.max(...groupAdds.map(a => a.price * a.quantity));
      } else {
        // Soma normal
        total += groupAdds.reduce((sum, a) => sum + (a.price * a.quantity), 0);
      }
    });
    
    return total;
  };
  
  // Delivery config (modo radius ou neighborhood)
  const [deliveryConfig, setDeliveryConfig] = useState({
    mode: 'neighborhood', // 'neighborhood' ou 'radius'
    radius_zones: [],
    max_delivery_radius: null,
    has_store_address: false
  });
  const [calculatedFee, setCalculatedFee] = useState(null); // Taxa calculada por raio
  const [calculatingFee, setCalculatingFee] = useState(false); // Loading do cálculo
  const [feeError, setFeeError] = useState(''); // Erro no cálculo da taxa
  
  // Ref para tracking do último endereço calculado (evita re-cálculos desnecessários)
  const lastCalculatedAddressRef = useRef('');
  
  // Customer info states
  const [customerName, setCustomerName] = useState('');
  const [customerPhoneInput, setCustomerPhoneInput] = useState('');
  const [orderHistory, setOrderHistory] = useState([]);
  const [toastMessage, setToastMessage] = useState(null);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);

  // Refs para inputs (uncontrolled components)
  const customerNameRef = useRef(null);
  const customerPhoneRef = useRef(null);
  const cepRef = useRef(null); // Ref para CEP
  const streetRef = useRef(null);
  const numberRef = useRef(null);
  const complementRef = useRef(null);
  const neighborhoodRef = useRef(null); // Ref para bairro no modo radius
  const cashbackPhoneRef = useRef(null);
  const cashbackAmountRef = useRef(null);
  const changeAmountRef = useRef(null);

  // Memoized functions
  const loadOrderHistory = React.useCallback(async (phone) => {
    if (!phone) return;
    
    try {
      const response = await axios.get(`${API}/public/orders/${subdomain}/${phone}`);
      setOrderHistory(response.data);
    } catch (error) {
      console.error('Error loading order history:', error);
      setOrderHistory([]);
    }
  }, [subdomain]);

  const loadCustomerData = React.useCallback(() => {
    // Load customer data from localStorage silently
    const savedName = localStorage.getItem('customer_name');
    const savedPhone = localStorage.getItem('customer_phone');
    const savedStreet = localStorage.getItem('customer_street');
    const savedNumber = localStorage.getItem('customer_number');
    const savedComplement = localStorage.getItem('customer_complement');
    const savedNeighborhood = localStorage.getItem('customer_neighborhood');
    const savedCep = localStorage.getItem('customer_cep');
    const savedCity = localStorage.getItem('customer_city');
    const savedState = localStorage.getItem('customer_state');
    
    if (savedName) setCustomerName(savedName);
    if (savedPhone) {
      setCustomerPhone(savedPhone);
      setCustomerPhoneInput(savedPhone);
      // Load order history
      loadOrderHistory(savedPhone);
    }
    
    // Load address data
    if (savedStreet || savedNumber || savedComplement || savedNeighborhood || savedCep) {
      setAddress({
        cep: savedCep || '',
        street: savedStreet || '',
        number: savedNumber || '',
        complement: savedComplement || '',
        neighborhood: savedNeighborhood || '',
        city: savedCity || '',
        state: savedState || ''
      });
      
      // Se tiver bairro salvo, encontrar a zona correspondente
      if (savedNeighborhood && deliveryZones.length > 0) {
        const zone = deliveryZones.find(z => z.name === savedNeighborhood);
        if (zone) setSelectedZone(zone);
      }
    }
  }, [loadOrderHistory, deliveryZones]);
  
  const saveCustomerData = React.useCallback((name, phone) => {
    // Save customer data silently - don't trigger re-render
    if (name) localStorage.setItem('customer_name', name);
    if (phone) localStorage.setItem('customer_phone', phone);
  }, []);
  
  const saveAddressData = React.useCallback((addressData) => {
    // Save address data silently
    if (addressData.street) localStorage.setItem('customer_street', addressData.street);
    if (addressData.number) localStorage.setItem('customer_number', addressData.number);
    if (addressData.complement !== undefined) {
      if (addressData.complement) {
        localStorage.setItem('customer_complement', addressData.complement);
      } else {
        localStorage.removeItem('customer_complement');
      }
    }
    if (addressData.neighborhood) localStorage.setItem('customer_neighborhood', addressData.neighborhood);
    if (addressData.cep) localStorage.setItem('customer_cep', addressData.cep);
    if (addressData.city) localStorage.setItem('customer_city', addressData.city);
    if (addressData.state) localStorage.setItem('customer_state', addressData.state);
  }, []);

  // 📮 Buscar endereço pelo CEP (ViaCEP API)
  const fetchAddressByCep = React.useCallback(async (cep) => {
    // Limpar CEP (remover traços e espaços)
    const cleanCep = cep.replace(/\D/g, '');
    
    if (cleanCep.length !== 8) {
      return; // CEP incompleto
    }
    
    setLoadingCep(true);
    
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);
      
      if (response.data && !response.data.erro) {
        const { logradouro, bairro, localidade, uf } = response.data;
        
        // Atualizar state com os dados do CEP
        setAddress(prev => ({
          ...prev,
          cep: cleanCep,
          street: logradouro || prev.street,
          neighborhood: bairro || prev.neighborhood,
          city: localidade || prev.city,
          state: uf || prev.state
        }));
        
        // Atualizar os refs (para inputs defaultValue)
        if (streetRef.current && logradouro) {
          streetRef.current.value = logradouro;
        }
        if (neighborhoodRef.current && bairro) {
          neighborhoodRef.current.value = bairro;
        }
        
        // Salvar no localStorage
        saveAddressData({
          cep: cleanCep,
          street: logradouro,
          neighborhood: bairro,
          city: localidade,
          state: uf
        });
        
        // Focar no campo número após preencher
        if (numberRef.current) {
          numberRef.current.focus();
        }
        
        toast.success('Endereço encontrado!');
      } else {
        toast.error('CEP não encontrado');
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  }, [saveAddressData]);

  // 🗺️ Calcular taxa de entrega por raio (quando modo = radius)
  const calculateDeliveryFeeByRadius = React.useCallback(async (forceCalculate = false) => {
    if (!address.street || !address.number || !address.neighborhood) {
      if (!forceCalculate) {
        setFeeError('');
        setCalculatedFee(null);
      }
      return;
    }

    setCalculatingFee(true);
    setFeeError('');

    try {
      // Usar cidade/estado do endereço se disponível, senão usar do restaurante
      const city = address.city || restaurant?.city || '';
      const state = address.state || restaurant?.state || '';
      const fullAddress = `${address.street}, ${address.number}, ${address.neighborhood}, ${city}, ${state}, Brasil`;
      
      const response = await axios.post(`${API}/delivery-config/calculate-fee`, {
        address: fullAddress,
        subdomain: subdomain
      });

      if (response.data.success) {
        setCalculatedFee({
          fee: response.data.delivery_fee,
          distance: response.data.distance_km,
          message: response.data.message
        });
        setFeeError('');
      } else {
        setFeeError(response.data.message || 'Endereço fora da área de entrega');
        setCalculatedFee(null);
      }
    } catch (error) {
      console.error('Erro ao calcular taxa:', error);
      setFeeError(error.response?.data?.detail || 'Erro ao calcular taxa de entrega');
      setCalculatedFee(null);
    } finally {
      setCalculatingFee(false);
    }
  }, [address, restaurant, subdomain]);

  // 🚀 Auto-calcular taxa quando endereço estiver completo (modo radius)
  React.useEffect(() => {
    // Só calcular automaticamente no modo radius
    if (deliveryConfig.mode !== 'radius') return;
    // Só calcular se tiver endereço completo
    if (!address.street || !address.number || !address.neighborhood) return;
    
    // Criar uma key do endereço atual
    const currentAddressKey = `${address.street}|${address.number}|${address.neighborhood}`;
    
    // Se o endereço não mudou, não recalcular
    if (lastCalculatedAddressRef.current === currentAddressKey) return;
    
    // Debounce de 1000ms para evitar múltiplas chamadas durante digitação
    const timeoutId = setTimeout(() => {
      lastCalculatedAddressRef.current = currentAddressKey;
      calculateDeliveryFeeByRadius();
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [address.street, address.number, address.neighborhood, deliveryConfig.mode, calculateDeliveryFeeByRadius]);

  const loadCashbackData = React.useCallback(async (phone) => {
    if (!phone || !restaurant) return;
    
    try {
      const response = await axios.get(`${API}/public/cashback/${subdomain}/${phone}`);
      setCashbackData(response.data);
      setCustomerPhone(phone);
    } catch (error) {
      console.error('Error loading cashback:', error);
      setCashbackData(null);
    }
  }, [restaurant, subdomain]);

  // 📱 Detectar se é iOS ou Android
  const isIOS = () => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  };

  // 📲 Abrir WhatsApp de forma compatível com iOS e Android
  const openWhatsApp = (phoneNumber, message) => {
    if (!phoneNumber) {
      toast.error('Número do restaurante não configurado');
      return;
    }
    
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
    
    // Em dispositivos móveis, window.location.href funciona melhor
    // window.open pode ser bloqueado pelo navegador
    window.location.href = whatsappUrl;
  };


  // 🎟️ Validate Coupon
  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Digite um código de cupom');
      return;
    }

    try {
      setCouponLoading(true);
      setCouponError('');
      
      const subtotal = getCartTotal();
      
      // Use public endpoint with subdomain
      const response = await axios.post(`${API}/public/coupons/validate/${subdomain}`, {
        code: couponCode.toUpperCase(),
        order_value: subtotal,
        customer_phone: customerPhone || null
      });
      
      if (response.data.valid) {
        setAppliedCoupon(response.data.coupon);
        setCouponDiscount(response.data.discount_amount);
        setCouponError('');
        setShowCouponSuccess(true);
        
        // Hide success message after 3 seconds
        setTimeout(() => setShowCouponSuccess(false), 3000);
      } else {
        setCouponError(response.data.message);
        setAppliedCoupon(null);
        setCouponDiscount(0);
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponError(error.response?.data?.detail || 'Erro ao validar cupom');
      setAppliedCoupon(null);
      setCouponDiscount(0);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponCode('');
    setCouponError('');
    setShowCouponSuccess(false);
  };


  // Load data on mount
  useEffect(() => {
    loadMenuData();
  }, [subdomain]);

  useEffect(() => {
    loadCustomerData();
  }, [loadCustomerData]);

  // Sincronizar selectedZone quando deliveryZones forem carregadas e houver bairro salvo
  useEffect(() => {
    if (deliveryZones.length > 0 && address.neighborhood) {
      const zone = deliveryZones.find(z => z.name === address.neighborhood);
      if (zone && (!selectedZone || selectedZone.name !== zone.name)) {
        setSelectedZone(zone);
      }
    }
  }, [deliveryZones, address.neighborhood]);

  // Função para forçar refresh do cache
  const refreshCache = async () => {
    try {
      await axios.post(`${API}/public/refresh-cache/${subdomain}`);
      console.log('🔄 Cache refreshed, reloading data...');
      await loadMenuData();
    } catch (error) {
      console.error('Error refreshing cache:', error);
    }
  };

  // 🚀 CACHE LOCAL: Chave única para este restaurante
  const CACHE_KEY = `menu_cache_${subdomain}`;
  const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutos

  // Função para salvar dados no localStorage
  const saveToCache = (data) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      console.log('💾 Menu salvo no cache local');
    } catch (e) {
      console.warn('⚠️ Erro ao salvar cache:', e);
    }
  };

  // Função para carregar dados do cache
  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const isExpired = Date.now() - timestamp > CACHE_EXPIRY;
        return { data, isExpired };
      }
    } catch (e) {
      console.warn('⚠️ Erro ao ler cache:', e);
    }
    return null;
  };

  // 🔄 Função para comparar arrays de produtos/categorias (evita re-render se idênticos)
  const arraysAreEqual = (arr1, arr2) => {
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;
    // Comparar por ID para verificar se os itens são os mesmos
    const ids1 = arr1.map(item => item.id).sort().join(',');
    const ids2 = arr2.map(item => item.id).sort().join(',');
    return ids1 === ids2;
  };

  const loadMenuData = async (isRetry = false) => {
    try {
      // 🚀 OTIMIZAÇÃO 1: Carregar cache local IMEDIATAMENTE
      const cached = loadFromCache();
      if (cached && cached.data) {
        console.log('⚡ Carregando dados do cache local...');
        const { restaurant: cachedRestaurant, categories: cachedCategories, products: cachedProducts, 
                additionals: cachedAdditionals, additionalGroups: cachedAdditionalGroups,
                deliveryZones: cachedZones, layout: cachedLayout, deliveryConfig: cachedDeliveryConfig,
                featuredProducts: cachedFeatured } = cached.data;
        
        // Aplicar dados do cache imediatamente
        if (cachedRestaurant) setRestaurant(cachedRestaurant);
        if (cachedCategories) setCategories(cachedCategories);
        if (cachedProducts) setProducts(cachedProducts);
        if (cachedAdditionals) setAdditionals(cachedAdditionals);
        if (cachedAdditionalGroups) setAdditionalGroups(cachedAdditionalGroups);
        if (cachedZones) setDeliveryZones(cachedZones);
        if (cachedLayout) setLayout(cachedLayout);
        if (cachedDeliveryConfig) setDeliveryConfig(cachedDeliveryConfig);
        if (cachedFeatured) setFeaturedProducts(cachedFeatured);
        
        // Se o cache não expirou, apenas atualiza em background
        if (!cached.isExpired) {
          setLoading(false);
          console.log('✅ Cache válido - atualizando em background');
          // Atualiza em background sem mostrar loading
          fetchFreshData(false);
          return;
        } else {
          console.log('⏰ Cache expirado - buscando dados frescos');
        }
      }
      
      setLoading(true);
      await fetchFreshData(true);
      
    } catch (err) {
      console.error('Error loading menu data:', err);
      setError('Erro ao carregar cardápio');
      setLoading(false);
    }
  };

  // Função separada para buscar dados frescos do servidor
  const fetchFreshData = async (showLoading = true) => {
    try {
      // Add timestamp to force cache bypass
      const timestamp = new Date().getTime();
      const cacheHeaders = {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      };
      
      // 🚀 OTIMIZAÇÃO: Carregar TUDO em uma única chamada
      try {
        const fullMenuRes = await axios.get(`${API}/public/menu/${subdomain}/full?t=${timestamp}`, cacheHeaders);
        const data = fullMenuRes.data;
        
        // 🔄 ANTI-FLICKER: Só atualizar estado se dados realmente mudaram
        // Isso evita re-render desnecessário quando dados do servidor são iguais ao cache
        setRestaurant(prev => {
          if (prev && data.restaurant && prev.id === data.restaurant.id) {
            // Verificar se algo importante mudou
            if (JSON.stringify(prev.status) === JSON.stringify(data.restaurant.status)) {
              return prev; // Manter referência anterior para evitar re-render
            }
          }
          return data.restaurant;
        });
        
        setCategories(prev => arraysAreEqual(prev, data.categories) ? prev : data.categories);
        setProducts(prev => arraysAreEqual(prev, data.products) ? prev : data.products);
        setAdditionals(prev => arraysAreEqual(prev, data.additionals) ? prev : data.additionals);
        setAdditionalGroups(prev => arraysAreEqual(prev, data.additional_groups) ? prev : data.additional_groups);
        setDeliveryZones(prev => arraysAreEqual(prev, data.delivery_zones) ? prev : data.delivery_zones);
        setLayout(prev => prev === (data.layout || 'list') ? prev : (data.layout || 'list'));
        
        console.log('🚀 Menu carregado - atualizações aplicadas apenas se necessário');
        
        // Objeto para salvar no cache
        let cacheData = {
          restaurant: data.restaurant,
          categories: data.categories,
          products: data.products,
          additionals: data.additionals,
          additionalGroups: data.additional_groups,
          deliveryZones: data.delivery_zones,
          layout: data.layout || 'list'
        };
        
        // 🗺️ Carregar configuração de delivery (modo neighborhood vs radius)
        try {
          const configRes = await axios.get(`${API}/public/delivery-config/${subdomain}?t=${timestamp}`, cacheHeaders);
          setDeliveryConfig(configRes.data);
          cacheData.deliveryConfig = configRes.data;
          console.log('🗺️ Delivery config:', configRes.data.mode);
        } catch (configError) {
          console.warn('⚠️ Delivery config não encontrada, usando padrão (neighborhood)');
          const defaultConfig = { mode: 'neighborhood', radius_zones: [], max_delivery_radius: null, has_store_address: false };
          setDeliveryConfig(defaultConfig);
          cacheData.deliveryConfig = defaultConfig;
        }
        
        // Carregar produtos em destaque
        try {
          const featuredRes = await axios.get(`${API}/public/restaurant/${subdomain}/featured-products?t=${timestamp}`, cacheHeaders);
          // 🔄 ANTI-FLICKER: Só atualizar se mudou
          setFeaturedProducts(prev => arraysAreEqual(prev, featuredRes.data || []) ? prev : (featuredRes.data || []));
          cacheData.featuredProducts = featuredRes.data || [];
          console.log(`✨ ${featuredRes.data?.length || 0} produtos em destaque carregados`);
        } catch (featuredError) {
          console.warn('⚠️ Erro ao carregar produtos em destaque:', featuredError.message);
          setFeaturedProducts(prev => prev.length === 0 ? prev : []);
          cacheData.featuredProducts = [];
        }
        
        // 💾 Salvar no cache local
        saveToCache(cacheData);
        
      } catch (fullMenuError) {
        // Fallback para chamadas individuais se endpoint unificado falhar
        console.warn('⚠️ Usando fallback para chamadas individuais:', fullMenuError.message);
        
        // Load restaurant info
        const restaurantRes = await axios.get(`${API}/public/restaurant/${subdomain}?t=${timestamp}`, cacheHeaders);
        setRestaurant(prev => {
          if (prev && restaurantRes.data && prev.id === restaurantRes.data.id) {
            return prev;
          }
          return restaurantRes.data;
        });
        
        // Load layout preference
        try {
          const appearanceRes = await axios.get(`${API}/public/restaurant/${subdomain}/appearance?t=${timestamp}`, cacheHeaders);
          setLayout(prev => prev === (appearanceRes.data.layout || 'list') ? prev : (appearanceRes.data.layout || 'list'));
        } catch (error) {
          setLayout(prev => prev === 'list' ? prev : 'list');
        }
        
        // Carregar resto em paralelo para ser mais rápido
        const [categoriesRes, productsRes, additionalsRes] = await Promise.all([
          axios.get(`${API}/public/menu/${subdomain}/categories?t=${timestamp}`, cacheHeaders),
          axios.get(`${API}/public/menu/${subdomain}/products?t=${timestamp}`, cacheHeaders),
          axios.get(`${API}/public/menu/${subdomain}/additionals?t=${timestamp}`, cacheHeaders)
        ]);
        
        // 🔄 ANTI-FLICKER: Só atualizar se dados mudaram
        setCategories(prev => arraysAreEqual(prev, categoriesRes.data) ? prev : categoriesRes.data);
        setProducts(prev => arraysAreEqual(prev, productsRes.data) ? prev : productsRes.data);
        setAdditionals(prev => arraysAreEqual(prev, additionalsRes.data) ? prev : additionalsRes.data);
        
        // Load additional groups
        try {
          const groupsRes = await axios.get(`${API}/public/menu/${subdomain}/additional-groups?t=${timestamp}`, cacheHeaders);
          setAdditionalGroups(prev => arraysAreEqual(prev, groupsRes.data) ? prev : groupsRes.data);
        } catch (error) {
          setAdditionalGroups(prev => prev.length === 0 ? prev : []);
        }
        
        // Load delivery configuration (mode: neighborhood ou radius)
        try {
          const configRes = await axios.get(`${API}/public/delivery-config/${subdomain}?t=${timestamp}`, cacheHeaders);
          setDeliveryConfig(configRes.data);
        } catch (error) {
          // Fallback para modo padrão (neighborhood)
          setDeliveryConfig({ mode: 'neighborhood', radius_zones: [], max_delivery_radius: null, has_store_address: false });
        }
        
        // Load delivery zones (para modo neighborhood)
        try {
          const zonesRes = await axios.get(`${API}/public/delivery-zones/${subdomain}?t=${timestamp}`, cacheHeaders);
          setDeliveryZones(prev => arraysAreEqual(prev, zonesRes.data) ? prev : zonesRes.data);
        } catch (error) {
          setDeliveryZones(prev => prev.length === 0 ? prev : []);
        }
        
        // Carregar produtos em destaque (fallback)
        try {
          const featuredRes = await axios.get(`${API}/public/restaurant/${subdomain}/featured-products?t=${timestamp}`, cacheHeaders);
          setFeaturedProducts(prev => arraysAreEqual(prev, featuredRes.data || []) ? prev : (featuredRes.data || []));
        } catch (featuredError) {
          setFeaturedProducts(prev => prev.length === 0 ? prev : []);
        }
      }
      
      // Load cashback data if customer phone is available
      const savedPhone = localStorage.getItem('customer_phone');
      if (savedPhone) {
        try {
          const cashbackRes = await axios.get(`${API}/public/cashback/${subdomain}/${savedPhone}?t=${timestamp}`, cacheHeaders);
          setCashbackData(cashbackRes.data);
        } catch (error) {
          console.error('Error loading cashback data:', error);
          // Não mostra erro para o usuário, apenas não carrega o cashback
        }
      }
      
      // 🎟️ Check if restaurant has active coupons
      try {
        const couponsCheckRes = await axios.get(`${API}/public/coupons/active-check/${subdomain}?t=${timestamp}`, cacheHeaders);
        setHasActiveCoupons(couponsCheckRes.data.has_active_coupons || false);
      } catch (error) {
        console.error('Error checking active coupons:', error);
        setHasActiveCoupons(false);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading menu data:', error);
      setLoading(false);
      // Mostrar erro em vez de dados falsos
      setError('Não foi possível carregar o cardápio. Tente novamente.');
    }
  };

  // 📅 Load scheduling slots
  const loadSchedulingSlots = async () => {
    try {
      setLoadingSlots(true);
      const response = await axios.get(`${API}/public/menu/${subdomain}/scheduling-slots`);
      setSchedulingSlots(response.data.slots || []);
      return response.data;
    } catch (error) {
      console.error('Error loading scheduling slots:', error);
      setSchedulingSlots([]);
      return { scheduling_available: false };
    } finally {
      setLoadingSlots(false);
    }
  };

  // Check if scheduling is needed and show modal
  const checkSchedulingRequired = async () => {
    const isClosed = !restaurant?.status?.is_open || restaurant?.manually_closed;
    const schedulingEnabled = restaurant?.scheduling?.enabled;
    const schedulingWhenOpen = restaurant?.scheduling?.when_open;
    
    // If store is closed and scheduling is enabled
    if (isClosed && schedulingEnabled) {
      const slotsData = await loadSchedulingSlots();
      if (slotsData.scheduling_available) {
        setShowSchedulingModal(true);
        return true;
      } else {
        setShowClosedStoreModal(true);
        return true;
      }
    }
    
    // If store is open but scheduling when open is enabled, optionally offer scheduling
    if (!isClosed && schedulingWhenOpen && schedulingEnabled) {
      // Allow immediate order or scheduled
      return false;
    }
    
    return false;
  };

  const addToCart = (product, selectedAdditionals = {}, notes = '') => {
    // Check if restaurant is open or scheduling is available
    const isClosed = !restaurant?.status?.is_open || restaurant?.manually_closed;
    const schedulingEnabled = restaurant?.scheduling?.enabled;
    
    if (isClosed && !schedulingEnabled) {
      setShowClosedStoreModal(true);
      return;
    }
    
    const additionalsList = Object.values(selectedAdditionals).filter(add => add.quantity > 0);
    const additionalTotal = calculateAdditionalsTotal(selectedAdditionals);
    
    const cartItem = {
      id: `${product.id}-${Date.now()}`,
      product,
      additionals: additionalsList,
      selectedAdditionalsMap: selectedAdditionals, // Guardar mapa para recalcular se necessário
      quantity: 1,
      totalPrice: product.price + additionalTotal,
      notes: notes || ''
    };
    setCart([...cart, cartItem]);
    setShowCartConfirmation(true);
  };

  const updateCartItemQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      setCart(cart.filter(item => item.id !== itemId));
    } else {
      setCart(cart.map(item => {
        if (item.id === itemId) {
          // Usar o mapa de adicionais se disponível, senão calcular da lista
          const additionalTotal = item.selectedAdditionalsMap 
            ? calculateAdditionalsTotal(item.selectedAdditionalsMap)
            : item.additionals.reduce((sum, add) => sum + (add.price * add.quantity), 0);
          return {
            ...item,
            quantity: newQuantity,
            totalPrice: (item.product.price + additionalTotal) * newQuantity
          };
        }
        return item;
      }));
    }
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  const getCartItemsCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  // Function to handle reordering from order history
  const handleReorder = (order) => {
    if (!order.items || order.items.length === 0) {
      showToast('Não foi possível encontrar os itens deste pedido.', 'error');
      return;
    }

    // Check if restaurant is open or scheduling is available
    const isClosed = !restaurant?.status?.is_open || restaurant?.manually_closed;
    const schedulingEnabled = restaurant?.scheduling?.enabled;
    
    if (isClosed && !schedulingEnabled) {
      setShowClosedStoreModal(true);
      return;
    }

    const unavailableItems = [];
    const itemsToAdd = [];

    // Verify each item from the order - SEMPRE buscar preço ATUAL do banco de dados
    order.items.forEach(orderItem => {
      // Buscar pelo ID primeiro (mais confiável), fallback para nome
      let product = null;
      if (orderItem.product_id) {
        product = products.find(p => p.id === orderItem.product_id && p.available);
      }
      if (!product) {
        product = products.find(p => p.name === orderItem.product_name && p.available);
      }
      
      if (!product) {
        unavailableItems.push(orderItem.product_name);
      } else {
        // Recalcular adicionais com preços ATUAIS do banco
        const currentAdditionals = [];
        const currentAdditionalsMap = {};
        
        if (orderItem.additionals && orderItem.additionals.length > 0) {
          orderItem.additionals.forEach(oldAdd => {
            // Buscar adicional atual em todos os grupos disponíveis
            let currentAdd = null;
            let currentGroupId = null;
            
            for (const group of additionalGroups) {
              if (group.additionals) {
                const found = group.additionals.find(a => 
                  (oldAdd.id && a.id === oldAdd.id) ||
                  (a.name === (oldAdd.name || oldAdd.additional_name))
                );
                if (found) {
                  currentAdd = found;
                  currentGroupId = group.id;
                  break;
                }
              }
            }
            
            if (currentAdd) {
              // Usar SEMPRE o preço atual do adicional
              const additionalWithCurrentPrice = {
                id: currentAdd.id,
                name: currentAdd.name,
                price: currentAdd.price,  // PREÇO ATUAL
                quantity: oldAdd.quantity || 1,
                group_id: currentGroupId,
                group_name: currentAdd.group_name || oldAdd.group_name
              };
              currentAdditionals.push(additionalWithCurrentPrice);
              currentAdditionalsMap[`${currentGroupId}_${currentAdd.id}`] = additionalWithCurrentPrice;
            }
          });
        }
        
        // Calcular total dos adicionais com preços ATUAIS (considerando cobrar_mais_caro)
        const additionalTotal = calculateAdditionalsTotal(currentAdditionalsMap);
        
        // Create cart item usando SEMPRE o preço ATUAL do produto
        const cartItem = {
          id: `${product.id}-${Date.now()}-${Math.random()}`,
          product: product,
          additionals: currentAdditionals,
          selectedAdditionalsMap: currentAdditionalsMap,
          quantity: orderItem.quantity,
          totalPrice: (product.price + additionalTotal) * orderItem.quantity,  // PREÇO ATUAL recalculado
          notes: orderItem.notes || orderItem.observation || ''
        };
        itemsToAdd.push(cartItem);
      }
    });

    // Show message if some items are unavailable
    if (unavailableItems.length > 0) {
      if (unavailableItems.length === order.items.length) {
        showToast('😔 Infelizmente, nenhum dos produtos deste pedido está disponível no momento.', 'error');
      } else {
        showToast(`😔 Alguns produtos não estão mais disponíveis: ${unavailableItems.join(', ')}. Os demais itens foram adicionados ao carrinho com preços atualizados.`, 'warning');
        setCart([...cart, ...itemsToAdd]);
        setCurrentScreen('cart');
      }
    } else {
      // All items available, add to cart with CURRENT prices
      setCart([...cart, ...itemsToAdd]);
      showToast('✅ Produtos adicionados ao carrinho com preços atualizados!', 'success');
      setCurrentScreen('cart');
    }
  };

  // Function to show toast messages
  const showToast = (message, type = 'info') => {
    setToastMessage({ text: message, type });
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Function to get next opening time
  const getNextOpeningInfo = () => {
    if (!restaurant?.status?.schedule) return null;

    const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayNames = {
      sunday: 'Domingo',
      monday: 'Segunda-feira',
      tuesday: 'Terça-feira',
      wednesday: 'Quarta-feira',
      thursday: 'Quinta-feira',
      friday: 'Sexta-feira',
      saturday: 'Sábado'
    };

    const now = new Date();
    const currentDay = daysOfWeek[now.getDay()];
    const currentTime = now.getHours() * 60 + now.getMinutes();

    // Check today's schedule first
    const todaySchedule = restaurant.status.schedule[currentDay];
    if (todaySchedule?.enabled) {
      const [openHour, openMin] = todaySchedule.open.split(':').map(Number);
      const openTime = openHour * 60 + openMin;
      
      if (currentTime < openTime) {
        return {
          day: 'hoje',
          time: todaySchedule.open,
          fullDay: dayNames[currentDay]
        };
      }
    }

    // Check next 7 days
    for (let i = 1; i <= 7; i++) {
      const checkDayIndex = (now.getDay() + i) % 7;
      const checkDay = daysOfWeek[checkDayIndex];
      const schedule = restaurant.status.schedule[checkDay];

      if (schedule?.enabled) {
        const dayLabel = i === 1 ? 'amanhã' : dayNames[checkDay];
        return {
          day: dayLabel,
          time: schedule.open,
          fullDay: dayNames[checkDay]
        };
      }
    }

    return null;
  };

  const handleWhatsAppOrder = async () => {
    // Prevenir múltiplos cliques
    if (isSubmittingOrder) {
      return;
    }
    
    try {
      setIsSubmittingOrder(true);
      
      // Check if restaurant is suspended or expired before allowing order
      const isSuspended = restaurant?.status?.is_suspended === true;
      const isExpired = restaurant?.status?.is_expired === true;
      const isClosed = !restaurant?.status?.is_open || restaurant?.manually_closed;
      
      // If store is closed and not a scheduled order, check if scheduling is available
      if ((isSuspended || isExpired) || (isClosed && !isScheduledOrder)) {
        const schedulingEnabled = restaurant?.scheduling?.enabled;
        if (isClosed && schedulingEnabled && !isScheduledOrder) {
          // Show scheduling modal instead
          const slotsData = await loadSchedulingSlots();
          if (slotsData.scheduling_available) {
            setShowSchedulingModal(true);
            setIsSubmittingOrder(false);
            return;
          }
        }
        setShowClosedStoreModal(true);
        setIsSubmittingOrder(false);
        return;
      }
      
      // ===== VALIDAÇÃO OBRIGATÓRIA DO BAIRRO PARA DELIVERY =====
      if (deliveryType === 'delivery') {
        // Verificar se o bairro foi preenchido
        if (!address.neighborhood || address.neighborhood.trim() === '') {
          toast.error('Por favor, informe o bairro de entrega');
          setCheckoutStep(3);
          setIsSubmittingOrder(false);
          return;
        }
        
        // Validação específica por modo de entrega
        if (deliveryConfig.mode === 'neighborhood') {
          // Modo bairro: precisa ter zonas cadastradas e bairro válido
          const validZone = deliveryZones.find(z => z.name === address.neighborhood);
          if (!validZone && deliveryZones.length > 0) {
            toast.error('Bairro inválido. Por favor, selecione um bairro da lista');
            setCheckoutStep(3);
            setIsSubmittingOrder(false);
            return;
          }
          
          if (deliveryZones.length === 0) {
            toast.error('Desculpe, não há bairros disponíveis para entrega no momento');
            setIsSubmittingOrder(false);
            return;
          }
        } else if (deliveryConfig.mode === 'radius') {
          // Modo raio: precisa ter calculado a taxa
          if (!calculatedFee) {
            toast.error('Por favor, calcule a taxa de entrega antes de finalizar');
            setCheckoutStep(3);
            setIsSubmittingOrder(false);
            return;
          }
        }
      }
      // ===== FIM DA VALIDAÇÃO =====
      
      // Pegar valores dos refs
      const finalCustomerName = customerNameRef.current?.value || customerName || 'Cliente Web';
      const finalCustomerPhone = customerPhoneRef.current?.value || customerPhone;
      const finalStreet = streetRef.current?.value || address.street;
      const finalNumber = numberRef.current?.value || address.number;
      const finalComplement = complementRef.current?.value || address.complement;
      
      const subtotal = getCartTotal();
      // Taxa de entrega: modo radius usa calculatedFee, modo neighborhood usa selectedZone
      const deliveryFee = deliveryType === 'delivery' 
        ? (deliveryConfig.mode === 'radius' 
            ? (calculatedFee?.fee ?? 0) 
            : (selectedZone?.fee ?? 0))
        : 0;
      const cashbackUsed = useCashback ? (parseFloat(cashbackAmountRef.current?.value) || cashbackAmount) : 0;
      const couponDiscountUsed = appliedCoupon ? couponDiscount : 0;
      const totalBeforeCashback = subtotal + deliveryFee;
      const finalTotal = totalBeforeCashback - cashbackUsed - couponDiscountUsed;
      
      // Create order data
      const orderData = {
        customer_name: finalCustomerName,
        customer_phone: finalCustomerPhone,
        items: cart.map(item => ({
          product_id: item.product.id,
          product_name: item.product.name,
          quantity: item.quantity,
          price: item.product.price,
          additionals: item.additionals.map(add => ({
            additional_id: add.id,
            additional_name: add.name,
            quantity: add.quantity,
            price: add.price
          })),
          notes: item.notes || ''
        })),
        total: finalTotal,
        subtotal: subtotal,
        delivery_fee: deliveryFee,
        delivery_zone_name: deliveryType === 'delivery' ? (selectedZone?.name || address.neighborhood || '') : null,
        cashback_used: cashbackUsed,
        coupon_code: appliedCoupon?.code || null,
        coupon_discount: couponDiscountUsed,
        delivery_type: deliveryType,
        address: deliveryType === 'delivery' ? {
          ...address,
          street: finalStreet,
          number: finalNumber,
          complement: finalComplement,
          // Garantir que o neighborhood venha do selectedZone (modo bairro) ou do address
          neighborhood: selectedZone?.name || address.neighborhood || ''
        } : {},
        payment_method: paymentMethod,
        change_for: paymentMethod === 'dinheiro' && needsChange ? parseFloat(changeAmountRef.current?.value || changeAmount || 0) : null,
        // Scheduling data
        is_scheduled: isScheduledOrder,
        scheduled_date: isScheduledOrder ? selectedScheduleDate : null,
        scheduled_time: isScheduledOrder ? selectedScheduleTime : null
      };

      // Se for PIX Mercado Pago, abrir modal
      if (paymentMethod === 'pix_mp') {
        setPixOrderData(orderData);
        setShowPixCheckout(true);
        setIsSubmittingOrder(false);
        return;
      }

      // Para outros métodos de pagamento, criar pedido normalmente
      const orderResponse = await axios.post(`${API}/public/menu/${subdomain}/orders`, orderData);
      const orderNumber = orderResponse.data?.order_number || 'N/A';

      // Generate WhatsApp message
      let message = `🍔 PEDIDO ${restaurant?.name?.toUpperCase() || 'RESTAURANTE'}\n\n`;
      
      // Informações do Cliente
      message += `👤 CLIENTE:\n`;
      message += `Nome: ${finalCustomerName}\n`;
      message += `Telefone: ${finalCustomerPhone}\n\n`;
      
      message += `📝 ITENS DO PEDIDO:\n`;
      
      cart.forEach(item => {
        message += `• ${item.quantity}x ${item.product.name} - R$ ${item.totalPrice.toFixed(2)}\n`;
        item.additionals.forEach(add => {
          if (add.quantity > 0) {
            message += `  - ${add.quantity}x ${add.name}\n`;
          }
        });
        if (item.notes && item.notes.trim() !== '') {
          message += `  📌 Obs: ${item.notes}\n`;
        }
      });

      message += `\n💰 Subtotal: R$ ${subtotal.toFixed(2)}\n`;
      if (deliveryType === 'delivery') {
        message += `🚚 Taxa de entrega: R$ ${deliveryFee.toFixed(2)}\n`;
      }
      if (couponDiscountUsed > 0) {
        message += `🎟️ Cupom ${appliedCoupon.code}: -R$ ${couponDiscountUsed.toFixed(2)}\n`;
      }
      if (cashbackUsed > 0) {
        message += `✨ Cashback aplicado: -R$ ${cashbackUsed.toFixed(2)}\n`;
      }
      message += `💳 TOTAL: R$ ${finalTotal.toFixed(2)}\n\n`;

      if (deliveryType === 'delivery') {
        message += `🚚 Entrega: Delivery\n`;
        message += `📍 Endereço: ${finalStreet}, ${finalNumber} - ${address.neighborhood}\n`;
        if (finalComplement) {
          message += `   Complemento: ${finalComplement}\n`;
        }
      } else {
        message += `🏪 Retirada no Local\n`;
      }

      message += `\n💳 Pagamento: ${paymentMethod === 'dinheiro' ? 'Dinheiro na entrega' : paymentMethod === 'cartao' ? 'Cartão' : 'PIX'}`;
      
      // Adicionar informação de troco se necessário
      if (paymentMethod === 'dinheiro' && needsChange) {
        const trocoValue = changeAmountRef.current?.value || changeAmount;
        if (trocoValue) {
          message += `\n💵 Troco para: R$ ${parseFloat(trocoValue).toFixed(2)}`;
        }
      }
      
      message += `\n\n---\nPedido #${orderNumber} realizado pelo cardápio digital`;

      // Usar telefone do restaurante configurado nas configurações
      const phone = restaurant?.phone;
      
      // Se cashback foi usado, recarregar dados para refletir o novo saldo
      if (cashbackUsed > 0 && finalCustomerPhone) {
        await loadCashbackData(finalCustomerPhone);
      }
      
      // Preparar dados para tela de confirmação
      const confirmationData = {
        orderNumber: orderNumber,
        total: finalTotal,
        whatsappMessage: message,
        restaurantPhone: phone,
        restaurantName: restaurant?.name || 'Restaurante'
      };
      
      // Mostrar tela de confirmação
      setConfirmationOrderData(confirmationData);
      setShowOrderConfirmation(true);
      
      // Se for Android, abrir WhatsApp automaticamente após 1.5 segundos
      if (!isIOS() && phone) {
        setTimeout(() => {
          openWhatsApp(phone, message);
        }, 1500);
      }
      
      // Clear cart and reset states after successful order
      setCart([]);
      setUseCashback(false);
      setCashbackAmount(0);
      removeCoupon(); // Limpar cupom
      setCurrentScreen('home');
      
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Erro ao processar pedido. Tente novamente.');
      setIsSubmittingOrder(false);
    }
  };

  // Group products by category and sort by display_order (if set) then by price (ascending)
  const groupedProducts = categories.map(category => ({
    ...category,
    products: products
      .filter(product => product.category_id === category.id)
      .sort((a, b) => {
        // First check display_order - products with display_order come first
        const orderA = a.display_order;
        const orderB = b.display_order;
        
        // Both have display_order - sort by it
        if (orderA != null && orderA > 0 && orderB != null && orderB > 0) {
          return orderA - orderB;
        }
        
        // Only A has display_order - A comes first
        if (orderA != null && orderA > 0) {
          return -1;
        }
        
        // Only B has display_order - B comes first
        if (orderB != null && orderB > 0) {
          return 1;
        }
        
        // Neither has display_order - sort by price (ascending - cheapest first)
        const priceA = a.promotional_price ?? a.promo_price ?? a.sale_price ?? a.price;
        const priceB = b.promotional_price ?? b.promo_price ?? b.sale_price ?? b.price;
        
        if (priceA !== priceB) {
          return priceA - priceB;
        }
        
        // If prices are equal, sort alphabetically by name
        return a.name.localeCompare(b.name, 'pt-BR');
      })
  })).filter(category => category.products.length > 0);

  // Filter products by selected category
  // Mostrar todas as categorias - o filtro agora é apenas para destacar/scroll
  const filteredProducts = groupedProducts;

  // Handle category click - scroll to category section
  const handleCategoryClick = (categoryId) => {
    // Atualiza o estado visual (destaque no carrossel)
    setSelectedCategoryFilter(categoryId);
    
    // Agendar o scroll para depois do re-render
    setTimeout(() => {
      const scrollContainer = document.getElementById('home-scroll-container');
      if (!scrollContainer) {
        console.log('Container não encontrado');
        return;
      }
      
      if (categoryId === 'all') {
        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const categoryElement = document.getElementById(`category-${categoryId}`);
        if (categoryElement) {
          // Calcular posição usando getBoundingClientRect
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = categoryElement.getBoundingClientRect();
          const currentScroll = scrollContainer.scrollTop;
          
          // Posição relativa + scroll atual - offset para título ficar visível
          const targetPosition = currentScroll + (elementRect.top - containerRect.top) - 160;
          
          console.log('Scrolling to category:', categoryId, 'position:', targetPosition);
          
          scrollContainer.scrollTo({ 
            top: Math.max(0, targetPosition), 
            behavior: 'smooth' 
          });
        } else {
          console.log('Elemento da categoria não encontrado:', categoryId);
        }
      }
    }, 150);
  };

  // Search products function with debouncing
  const handleSearch = (e) => {
    const query = e.target.value;
    
    // Update query state for controlled input
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // If query is empty, clear results immediately
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    // Debounce the actual search by 300ms
    searchTimeoutRef.current = setTimeout(() => {
      const searchTerm = query.toLowerCase().trim();
      const results = [];
      
      // Search through all products
      categories.forEach(category => {
        const categoryProducts = products.filter(p => p.category_id === category.id);
        
        categoryProducts.forEach(product => {
          // Search in name and description
          if (
            product.name.toLowerCase().includes(searchTerm) ||
            (product.description && product.description.toLowerCase().includes(searchTerm))
          ) {
            results.push({
              ...product,
              categoryName: category.name
            });
          }
        });
      });
      
      // Sort search results by display_order (if set) then by price (ascending)
      results.sort((a, b) => {
        // First check display_order
        const orderA = a.display_order;
        const orderB = b.display_order;
        
        if (orderA != null && orderA > 0 && orderB != null && orderB > 0) {
          return orderA - orderB;
        }
        if (orderA != null && orderA > 0) return -1;
        if (orderB != null && orderB > 0) return 1;
        
        // Then sort by price
        const priceA = a.promotional_price ?? a.promo_price ?? a.sale_price ?? a.price;
        const priceB = b.promotional_price ?? b.promo_price ?? b.sale_price ?? b.price;
        
        if (priceA !== priceB) {
          return priceA - priceB;
        }
        return a.name.localeCompare(b.name, 'pt-BR');
      });
      
      setSearchResults(results);
    }, 300);
  };
  
  // Open search modal
  const openSearch = () => {
    setShowSearchModal(true);
    setSearchQuery('');
    setSearchResults([]);
  };
  
  // Close search modal
  const closeSearch = () => {
    setShowSearchModal(false);
    setSearchQuery('');
    setSearchResults([]);
    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  // Maintain focus on search input when modal is open
  useEffect(() => {
    if (showSearchModal && searchInputRef.current) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [showSearchModal]);

  // Header Component
  const Header = () => (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* Banner de Loja Fechada Manualmente */}
      {restaurant?.manually_closed && (
        <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 py-3">
          <div className="flex items-center justify-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
            <span className="font-semibold text-sm">
              🔒 LOJA FECHADA TEMPORARIAMENTE
            </span>
            <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
          </div>
          <p className="text-center text-xs text-white/90 mt-1">
            Não estamos aceitando pedidos no momento
          </p>
        </div>
      )}
      {/* Blue section - restaurant name/logo and icons */}
      <div className="bg-primary text-white px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Left side - Back button OR Logo + Name */}
          <div className="flex items-center space-x-2 flex-1">
            {currentScreen !== 'home' ? (
              <button 
                onClick={() => setCurrentScreen(currentScreen === 'product' ? 'home' : currentScreen === 'promos' ? 'home' : currentScreen === 'orders' ? 'home' : currentScreen === 'cart' ? 'home' : 'cart')}
                className="p-1"
              >
                <ArrowLeft size={20} />
              </button>
            ) : restaurant?.logo_url ? (
              // Logo circular no canto esquerdo
              <div className="w-10 h-10 bg-white rounded-full overflow-hidden flex-shrink-0 shadow-md">
                <img 
                  src={restaurant.logo_url} 
                  alt={restaurant.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Se a logo falhar, esconde o container
                    e.target.parentElement.style.display = 'none';
                  }}
                />
              </div>
            ) : null}
            
            {/* Restaurant/Screen Name */}
            <h1 className={`text-lg font-medium ${currentScreen === 'home' && !restaurant?.logo_url ? 'flex-1' : ''}`}>
              {currentScreen === 'home' && (restaurant?.name || 'Carregando...')}
              {currentScreen === 'product' && 'Detalhes do Produto'}
              {currentScreen === 'cart' && 'Carrinho'}
              {currentScreen === 'promos' && 'Promoções'}
              {currentScreen === 'orders' && 'Meus Pedidos'}
              {currentScreen === 'checkout' && 'Finalizar Pedido'}
            </h1>
          </div>
          
          {/* Right side - Action buttons */}
          <div className="flex items-center space-x-3">
            {currentScreen === 'checkout' && (
              <span className="text-sm font-medium">Total: R$ {(getCartTotal() + (deliveryType === 'delivery' ? (deliveryConfig.mode === 'radius' ? (calculatedFee?.fee || 0) : (selectedZone?.fee || 0)) : 0)).toFixed(2)}</span>
            )}
            {currentScreen === 'cart' && cart.length > 0 && (
              <button 
                onClick={clearCart} 
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 hover:text-red-600 transition-all duration-200 text-sm font-medium border border-red-100 hover:border-red-200 active:scale-95"
              >
                <Trash2 size={14} />
                <span>Limpar</span>
              </button>
            )}
            {currentScreen === 'home' && (
              <button 
                onClick={openSearch}
                className="p-2 hover:bg-white/10 rounded-full transition-all active:scale-95"
                aria-label="Buscar produtos"
              >
                <Search size={20} />
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* White section - store information */}
      {currentScreen === 'home' && restaurant && (
        <div className={`text-gray-700 px-4 py-px border-b border-gray-200 ${
          restaurant.status?.is_open ? 'bg-white' : 'bg-red-50'
        }`}>
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-1.5">
              {restaurant.status?.is_expired || restaurant.status?.is_suspended ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                  <span className="text-red-600 font-semibold">
                    {restaurant.status?.is_suspended ? '🔒 Loja Suspensa' : '⏰ Fechado - Renovação Pendente'}
                  </span>
                </>
              ) : restaurant.manually_closed ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
                  <span className="text-orange-600 font-semibold">
                    🔒 Fechado Temporariamente
                  </span>
                </>
              ) : restaurant.status?.is_auto ? (
                <>
                  <span className={`w-2 h-2 rounded-full ${
                    restaurant.status.is_open ? 'bg-green-500' : 'bg-red-500'
                  }`}></span>
                  <span className={restaurant.status.is_open ? 'text-gray-600' : 'text-red-600 font-medium'}>
                    {restaurant.status.message}
                  </span>
                </>
              ) : (
                <span className="text-gray-600">Aberto</span>
              )}
              {!restaurant.status?.is_expired && !restaurant.status?.is_suspended && (
                <>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-600">Pedido mín. R$ {restaurant.minimum_order?.toFixed(2) || '15,00'}</span>
                </>
              )}
            </div>
            <button 
              className="text-primary font-medium"
              onClick={() => setShowStoreProfile(true)}
            >
              Perfil da loja
            </button>
          </div>
        </div>
      )}

      {/* Categories horizontal scroll */}
      {currentScreen === 'home' && groupedProducts.length > 0 && (
        <div className="bg-white border-b border-gray-200 overflow-x-auto scrollbar-hide">
          <div className="flex px-4 space-x-4 min-w-max">
            <button
              id="cat-btn-all"
              onClick={() => handleCategoryClick('all')}
              className={`py-2 text-sm font-medium whitespace-nowrap relative transition-colors ${
                selectedCategoryFilter === 'all'
                  ? 'text-primary'
                  : 'text-gray-700'
              }`}
            >
              Todos
              {selectedCategoryFilter === 'all' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
              )}
            </button>
            {groupedProducts.map((category) => (
              <button
                key={category.id}
                id={`cat-btn-${category.id}`}
                onClick={() => handleCategoryClick(category.id)}
                className={`py-2 text-sm font-medium whitespace-nowrap relative transition-colors ${
                  selectedCategoryFilter === category.id
                    ? 'text-primary'
                    : 'text-gray-700'
                }`}
              >
                {category.name}
                {selectedCategoryFilter === category.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
  
  // Search Modal Component
  const SearchModal = () => {
    if (!showSearchModal) return null;
    
    return (
      <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm" onClick={closeSearch}>
        <div 
          className="absolute top-0 left-0 right-0 bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: '90vh', overflowY: 'auto' }}
        >
          {/* Search Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <button 
                onClick={closeSearch}
                className="p-2 hover:bg-gray-100 rounded-full transition-all active:scale-95"
              >
                <ArrowLeft size={20} />
              </button>
              
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" 
                />
                <input
                  ref={searchInputRef}
                  id="search-input"
                  type="text"
                  placeholder="Buscar produtos..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                      searchInputRef.current?.focus();
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                  >
                    <X size={16} className="text-gray-500" />
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Search Results */}
          <div className="p-4">
            {searchQuery.trim() === '' ? (
              <div className="text-center py-12">
                <Search size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">Digite para buscar produtos</p>
                <p className="text-sm text-gray-400 mt-1">Ex: hambúrguer, pizza, suco...</p>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-3">😕</div>
                <p className="text-gray-700 font-medium">Nenhum produto encontrado</p>
                <p className="text-sm text-gray-500 mt-1">Tente buscar com outros termos</p>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold" style={{ color: 'var(--color-primary, #3B82F6)' }}>
                      {searchResults.length}
                    </span>
                    {' '}resultado{searchResults.length !== 1 ? 's' : ''} encontrado{searchResults.length !== 1 ? 's' : ''}
                  </p>
                </div>
                
                <div className="space-y-3">
                  {searchResults.map(product => (
                    <div
                      key={product.id}
                      onClick={() => {
                        setSelectedProduct(product);
                        setCurrentScreen('product');
                        closeSearch();
                      }}
                      className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3 cursor-pointer hover:border-primary hover:shadow-md transition-all active:scale-98"
                    >
                      {/* Product Image */}
                      <img
                        src={product.image_url || "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg"}
                        alt={product.name}
                        className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                        loading="eager"
                        onError={(e) => {
                          e.target.src = "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg";
                        }}
                      />
                      
                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1 truncate">
                          {product.name}
                        </h3>
                        <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                          {product.description || 'Produto delicioso'}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                              {product.categoryName}
                            </span>
                            {product.preparation_time && (
                              <span className="text-xs text-gray-400 flex items-center gap-0.5">
                                <Clock size={10} />
                                {product.preparation_time}min
                              </span>
                            )}
                          </div>
                          <span className="text-lg font-bold" style={{ color: 'var(--color-primary, #3B82F6)' }}>
                            {product.price > 0 ? `R$ ${product.price.toFixed(2)}` : 'Ver opções'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Arrow */}
                      <ChevronRight size={20} className="text-gray-400 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Bottom Navigation - apenas na tela home
  const BottomNav = () => {
    if (currentScreen !== 'home' && currentScreen !== 'promos' && currentScreen !== 'orders') return null;
    
    return (
      <div className="bg-white border-t border-gray-200 p-2 fixed bottom-0 left-0 right-0 z-50">
        <div className="flex items-center justify-around">
          <button 
            onClick={() => setCurrentScreen('home')}
            className={`flex flex-col items-center p-2 ${currentScreen === 'home' ? 'text-primary' : 'text-gray-600'}`}
            data-testid="bottom-nav-home"
          >
            <Home size={20} />
            <span className="text-xs mt-1">Início</span>
          </button>
          <button 
            onClick={() => setCurrentScreen('orders')}
            className={`flex flex-col items-center p-2 ${currentScreen === 'orders' ? 'text-primary' : 'text-gray-600'}`}
            data-testid="bottom-nav-orders"
          >
            <FileText size={20} />
            <span className="text-xs mt-1">Pedidos</span>
          </button>
          
          {/* Botão Promos - Só aparece se cashback estiver habilitado */}
          {cashbackData && cashbackData.enabled && (
            <button 
              onClick={() => setCurrentScreen('promos')}
              className={`flex flex-col items-center p-2 ${currentScreen === 'promos' ? 'text-primary' : 'text-gray-600'} relative`}
              data-testid="bottom-nav-promos"
            >
              <Tag size={20} />
              <span className="text-xs mt-1">Promos</span>
            </button>
          )}
          
          <button 
            onClick={() => setCurrentScreen('cart')}
            className={`flex flex-col items-center p-2 relative ${currentScreen === 'cart' ? 'text-primary' : 'text-gray-600'}`}
            data-testid="bottom-nav-cart"
          >
            <ShoppingCart size={20} />
            <span className="text-xs mt-1">Carrinho</span>
            {getCartItemsCount() > 0 && (
              <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {getCartItemsCount()}
              </div>
            )}
          </button>
        </div>
      </div>
    );
  };

  // Home Screen
  const HomeScreen = () => {
    // 🚀 SKELETON LOADER: Mostra estrutura enquanto carrega
    if (loading && products.length === 0) {
      return (
        <div className="pt-28 pb-20 px-4">
          {/* Header skeleton */}
          <div className="mb-6 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-3/4" />
          </div>
          
          {/* Featured section skeleton */}
          <div className="mb-8">
            <div className="h-5 bg-gray-200 rounded w-32 mb-4" />
            <div className="flex gap-3 overflow-x-hidden">
              {[1, 2, 3].map(i => (
                <div key={i} className="w-36 flex-shrink-0 animate-pulse">
                  <div className="aspect-square bg-gray-200 rounded-xl mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-1" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          </div>
          
          {/* Categories tabs skeleton */}
          <div className="flex gap-2 mb-6 overflow-x-hidden">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-8 w-24 bg-gray-200 rounded-full animate-pulse" />
            ))}
          </div>
          
          {/* Products skeleton */}
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <ProductSkeleton key={i} layout={layout} />
            ))}
          </div>
        </div>
      );
    }

    // Se não tem produtos mas a loja está expirada/suspensa, mostrar mensagem específica
    if (groupedProducts.length === 0) {
      const isExpiredOrSuspended = restaurant?.status?.is_expired || restaurant?.status?.is_suspended;
      
      if (isExpiredOrSuspended) {
        return (
          <div className="pt-28 pb-20 px-4 flex items-center justify-center min-h-screen">
            <div className="text-center max-w-sm mx-auto">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                {restaurant?.status?.is_suspended ? '🔒 Loja Suspensa' : '⏰ Loja Temporariamente Fechada'}
              </h2>
              <p className="text-gray-600 mb-4">
                Esta loja não está aceitando pedidos no momento.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Entre em contato com o estabelecimento para mais informações.
              </p>
              <button
                onClick={refreshCache}
                className="text-sm text-blue-500 hover:text-blue-700 underline"
              >
                Tentar atualizar cardápio
              </button>
            </div>
          </div>
        );
      }
      
      // Loja ativa mas sem produtos - oferecer botão de refresh
      return (
        <div className="pt-28 pb-20 px-4 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-gray-600 text-lg mb-4">Cardápio em atualização</p>
            <p className="text-gray-500 mb-6">Em breve novos produtos!</p>
            <button
              onClick={refreshCache}
              className="bg-primary text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-primary-dark transition-colors"
            >
              🔄 Atualizar cardápio
            </button>
          </div>
        </div>
      );
    }

    return (
      <div id="home-scroll-container" className={`fixed inset-0 pb-20 px-4 overflow-y-auto ${
        restaurant?.manually_closed ? 'pt-52' : 'pt-40'
      }`} style={{ WebkitOverflowScrolling: 'touch' }}>
        {/* Banner para Loja Expirada ou Suspensa */}
        {(restaurant?.status?.is_expired || restaurant?.status?.is_suspended) && (
          <div className="mb-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 shadow-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-base font-bold text-white">
                  {restaurant?.status?.is_suspended ? '🔒 Loja Suspensa' : '⏰ Loja Temporariamente Fechada'}
                </h3>
                <p className="text-sm text-white/90 mt-1">
                  Esta loja não está aceitando pedidos no momento. Você pode visualizar o cardápio normalmente.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Banner para Horário Fechado (apenas se NÃO for expirada/suspensa) */}
        {!restaurant?.status?.is_expired && !restaurant?.status?.is_suspended && restaurant?.status?.is_auto && !restaurant.status.is_open && (
          <div className={`mb-6 rounded-lg p-4 border-2 ${
            restaurant?.scheduling?.enabled && restaurant?.scheduling?.available
              ? 'bg-amber-50 border-amber-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  restaurant?.scheduling?.enabled && restaurant?.scheduling?.available
                    ? 'bg-amber-500'
                    : 'bg-red-500'
                }`}>
                  <Clock className="w-5 h-5 text-white" />
                </div>
              </div>
              <div className="ml-3 flex-1">
                <h3 className={`text-sm font-semibold ${
                  restaurant?.scheduling?.enabled && restaurant?.scheduling?.available
                    ? 'text-amber-800'
                    : 'text-red-800'
                }`}>
                  Estamos fechados no momento
                </h3>
                <p className={`text-sm mt-0.5 ${
                  restaurant?.scheduling?.enabled && restaurant?.scheduling?.available
                    ? 'text-amber-700'
                    : 'text-red-700'
                }`}>
                  {restaurant.status.message}
                </p>
              </div>
              {/* Badge de Agendamento Disponível */}
              {restaurant?.scheduling?.enabled && restaurant?.scheduling?.available && (
                <div className="flex-shrink-0 ml-2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Agendar
                  </span>
                </div>
              )}
            </div>
            {/* Mensagem extra quando agendamento está disponível */}
            {restaurant?.scheduling?.enabled && restaurant?.scheduling?.available && (
              <p className="text-xs text-amber-600 mt-2 ml-13 pl-[52px]">
                💡 Adicione itens ao carrinho e agende seu pedido para quando abrirmos!
              </p>
            )}
          </div>
        )}
        
        {/* Conditional Rendering: Grid or List */}
        {/* Featured Products Carousel - Exibir se houver produtos em destaque */}
        {featuredProducts && featuredProducts.length > 0 && (
          <FeaturedProductsCarousel
            products={featuredProducts}
            onProductClick={(product) => {
              setSelectedProduct(product);
              setCurrentScreen('product');
            }}
          />
        )}
        
        {layout === 'grid' ? (
          <ProductGridView 
            categories={filteredProducts}
            onProductClick={(product) => {
              setSelectedProduct(product);
              setCurrentScreen('product');
            }}
          />
        ) : (
          /* List View (Original) */
          filteredProducts.map(category => (
            <div key={category.id} id={`category-${category.id}`} className="mb-8">
              <div className="flex items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">{category.name}</h2>
              </div>
              <div className="space-y-4">
                {category.products.map(product => (
                  <div 
                    key={product.id} 
                    className="bg-white rounded-lg shadow-sm p-4 flex items-center space-x-4 transition-all duration-300 hover:shadow-md cursor-pointer"
                    onClick={() => {
                      setSelectedProduct(product);
                      setCurrentScreen('product');
                    }}
                  >
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{product.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <p className="text-lg font-bold text-gray-900">
                          {product.price > 0 ? `R$ ${product.price.toFixed(2)}` : 'Ver opções'}
                        </p>
                        {product.preparation_time && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock size={12} />
                            {product.preparation_time}min
                          </span>
                        )}
                      </div>
                    </div>
                    <LazyImage 
                      src={product.image_url || "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg"} 
                      alt={product.name}
                      className="w-24 h-24 rounded-lg flex-shrink-0"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  // Product Detail Screen
  const ProductScreen = () => {
    const [selectedAdditionals, setSelectedAdditionals] = useState({});
    const [groupSelections, setGroupSelections] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [validationMessage, setValidationMessage] = useState({ title: '', message: '' });
    const [productNotes, setProductNotes] = useState('');
    const [showMaxLimitModal, setShowMaxLimitModal] = useState(false);
    const [maxLimitMessage, setMaxLimitMessage] = useState({ groupName: '', maxChoices: 0 });
    
    // Refs e estados para auto-scroll de grupos obrigatórios
    const groupRefs = useRef({});
    const [nextRequiredGroupId, setNextRequiredGroupId] = useState(null);

    // Get product's additional groups
    const productGroups = React.useMemo(() => {
      if (!selectedProduct || !selectedProduct.additional_groups || selectedProduct.additional_groups.length === 0) {
        return [];
      }
      
      return selectedProduct.additional_groups
        .map(groupId => additionalGroups.find(g => g.id === groupId))
        .filter(Boolean);
    }, [selectedProduct, additionalGroups]);

    // Helper to create unique key for additional in a specific group
    const getAdditionalKey = (groupId, additionalId) => `${groupId}_${additionalId}`;

    const updateAdditionalInGroup = (groupId, additional, isSelected) => {
      const group = productGroups.find(g => g.id === groupId);
      if (!group) return;

      const currentGroupSelections = groupSelections[groupId] || [];
      const additionalKey = getAdditionalKey(groupId, additional.id);
      
      let newSelections;
      let newAdditionals = { ...selectedAdditionals };
      
      // If max_choices = 1 (radio behavior), replace selection
      if (group.max_choices === 1) {
        if (isSelected) {
          // Clear previous selection in this group (using composite keys)
          currentGroupSelections.forEach(prevId => {
            const prevKey = getAdditionalKey(groupId, prevId);
            delete newAdditionals[prevKey];
          });
          
          newSelections = [additional.id];
          newAdditionals[additionalKey] = { ...additional, quantity: 1, groupId };
        } else {
          newSelections = [];
          delete newAdditionals[additionalKey];
        }
      } else {
        // Multiple choice behavior
        if (isSelected) {
          // Check if we can add more
          if (currentGroupSelections.length >= group.max_choices) {
            alert(`Você pode escolher no máximo ${group.max_choices} ${group.max_choices === 1 ? 'item' : 'itens'} neste grupo`);
            return;
          }
          newSelections = [...currentGroupSelections, additional.id];
          newAdditionals[additionalKey] = { ...additional, quantity: 1, groupId };
        } else {
          newSelections = currentGroupSelections.filter(id => id !== additional.id);
          delete newAdditionals[additionalKey];
        }
      }

      setGroupSelections({
        ...groupSelections,
        [groupId]: newSelections
      });

      setSelectedAdditionals(newAdditionals);

      // Clear validation error for this group
      if (validationErrors[groupId]) {
        const newErrors = { ...validationErrors };
        delete newErrors[groupId];
        setValidationErrors(newErrors);
      }
    };

    const validateSelections = () => {
      const errors = {};
      
      productGroups.forEach(group => {
        const selections = groupSelections[group.id] || [];
        const count = selections.length;
        
        if (group.is_required && count < group.min_choices) {
          errors[group.id] = `Escolha pelo menos ${group.min_choices} ${group.min_choices === 1 ? 'item' : 'itens'}`;
        } else if (count < group.min_choices && count > 0) {
          errors[group.id] = `Escolha pelo menos ${group.min_choices} ${group.min_choices === 1 ? 'item' : 'itens'}`;
        }
      });
      
      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const getTotalPrice = () => {
      // Calcular o total dos adicionais considerando cobrar_mais_caro
      let additionalTotal = 0;
      
      // Agrupar adicionais selecionados por grupo
      const additionalsByGroup = {};
      Object.entries(selectedAdditionals).forEach(([key, add]) => {
        const [groupId] = key.split('_');
        if (!additionalsByGroup[groupId]) {
          additionalsByGroup[groupId] = [];
        }
        additionalsByGroup[groupId].push(add);
      });
      
      // Calcular o total de cada grupo
      Object.entries(additionalsByGroup).forEach(([groupId, groupAdditionals]) => {
        const group = productGroups.find(g => g.id === groupId);
        
        if (group && group.cobrar_mais_caro) {
          // Cobrar apenas o mais caro do grupo
          const maxPrice = Math.max(...groupAdditionals.map(add => add.price * add.quantity));
          additionalTotal += maxPrice;
        } else {
          // Soma normal
          additionalTotal += groupAdditionals.reduce((sum, add) => sum + (add.price * add.quantity), 0);
        }
      });
      
      return selectedProduct.price + additionalTotal;
    };

    // Função para encontrar o próximo grupo obrigatório não preenchido
    const findNextRequiredGroup = () => {
      for (const group of productGroups) {
        if (group.is_required) {
          const selections = groupSelections[group.id] || [];
          const minRequired = group.min_choices || 1;
          if (selections.length < minRequired) {
            return group.id;
          }
        }
      }
      return null;
    };

    // Função para fazer scroll suave até o próximo grupo obrigatório
    const scrollToNextRequiredGroup = () => {
      const nextGroupId = findNextRequiredGroup();
      if (nextGroupId && groupRefs.current[nextGroupId]) {
        setTimeout(() => {
          groupRefs.current[nextGroupId].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          groupRefs.current[nextGroupId].style.transition = 'all 0.3s ease';
          groupRefs.current[nextGroupId].style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.5)';
          setTimeout(() => {
            if (groupRefs.current[nextGroupId]) {
              groupRefs.current[nextGroupId].style.boxShadow = '';
            }
          }, 1500);
        }, 150);
      }
      setNextRequiredGroupId(nextGroupId);
    };

    // Handler para fechar o modal de validação e fazer scroll
    const handleCloseValidationModal = () => {
      setShowValidationModal(false);
      scrollToNextRequiredGroup();
    };

    const handleAddToCart = () => {
      // Criar validação inline para garantir que funciona
      const errors = {};
      
      productGroups.forEach(group => {
        const selections = groupSelections[group.id] || [];
        const count = selections.length;
        
        // Se o grupo é obrigatório e nenhum item foi selecionado
        if (group.is_required && count === 0) {
          errors[group.id] = `Escolha pelo menos ${group.min_choices || 1} ${(group.min_choices || 1) === 1 ? 'item' : 'itens'}`;
        } 
        // Se o grupo tem min_choices e não foi atingido
        else if (group.min_choices && count > 0 && count < group.min_choices) {
          errors[group.id] = `Escolha pelo menos ${group.min_choices} ${group.min_choices === 1 ? 'item' : 'itens'}`;
        }
      });
      
      // Atualizar os erros de validação no estado
      setValidationErrors(errors);
      
      // Se houver erros, mostrar modal e não prosseguir
      if (Object.keys(errors).length > 0) {
        const firstErrorGroup = productGroups.find(g => errors[g.id]);
        
        if (firstErrorGroup) {
          setValidationMessage({
            title: 'Campo Obrigatório',
            message: `O grupo "${firstErrorGroup.name}" é obrigatório.\n\n${errors[firstErrorGroup.id]}`
          });
        } else {
          setValidationMessage({
            title: 'Atenção',
            message: 'Existem campos obrigatórios que precisam ser preenchidos.'
          });
        }
        setShowValidationModal(true);
        return;
      }
      
      // Se passou na validação, adiciona ao carrinho
      addToCart(selectedProduct, selectedAdditionals, productNotes);
    };

    return (
      <div className="fixed inset-0 bg-white overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        {/* Imagem do Produto - Expansível in-place */}
        <div 
          className={`relative cursor-pointer overflow-hidden ${
            expandedImage ? 'h-96' : 'h-64'
          }`}
          style={{
            transition: 'height 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
          }}
          onClick={() => setExpandedImage(!expandedImage)}
        >
          <img 
            src={selectedProduct.image_url || "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg"} 
            alt={selectedProduct.name}
            className="w-full h-full object-cover"
            loading="eager"
            onError={(e) => {
              e.target.src = "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg";
            }}
          />
          {/* Indicador de expandir/retrair com animação suave */}
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2">
            <span className="text-white/90 text-xs bg-black/40 px-3 py-1.5 rounded-full backdrop-blur-sm flex items-center gap-1.5 shadow-lg">
              <ChevronUp 
                size={16} 
                style={{
                  transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  transform: expandedImage ? 'rotate(180deg)' : 'rotate(0deg)',
                  animation: expandedImage ? 'none' : 'soft-bounce 2s ease-in-out infinite'
                }}
              />
              {expandedImage ? 'Retrair' : 'Ampliar'}
            </span>
          </div>
          {/* Navegação Superior sobre a imagem */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/50 to-transparent">
            <button 
              onClick={(e) => { e.stopPropagation(); setCurrentScreen('home'); }}
              className="bg-black/30 backdrop-blur-sm text-white p-2 rounded-full hover:bg-black/50 transition-colors"
            >
              <ArrowLeft size={22} />
            </button>
          </div>
        </div>

        {/* Header - Informações do Produto */}
        <div className="bg-white px-4 py-4">
          <h1 className="text-2xl font-bold text-gray-900 leading-tight mb-2">
            {selectedProduct.name}
          </h1>
          <div className="flex items-center gap-3 mb-2">
            <p className="text-lg font-bold text-gray-900">
              {selectedProduct.price > 0 ? `R$ ${selectedProduct.price.toFixed(2)}` : 'Preço conforme opções'}
            </p>
            {selectedProduct.preparation_time && (
              <span className="text-sm text-gray-400 flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-full">
                <Clock size={14} />
                {selectedProduct.preparation_time} min
              </span>
            )}
          </div>
          {selectedProduct.description && (
            <p className="text-gray-600 text-sm leading-relaxed">
              {selectedProduct.description}
            </p>
          )}
        </div>

        {/* Área de Conteúdo - Fundo Claro */}
        <div className="bg-gray-50 px-4 pb-32 pt-2">
          {/* Grupos de Adicionais - Estilo Minimalista */}
          {productGroups.length > 0 && (
            <div className="space-y-1">
              {productGroups.map((group) => {
                const isSingleChoice = group.max_choices === 1;
                
                return (
                  <div key={group.id} ref={el => groupRefs.current[group.id] = el} data-group-id={group.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    {/* Group Header - Fundo Destacado com Melhor Hierarquia Visual */}
                    <div className="px-4 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 flex items-start gap-3">
                          {/* Ícone de Grupo */}
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center shadow-sm">
                              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                            </div>
                          </div>
                          
                          {/* Título e Descrição do Grupo */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-extrabold text-gray-900 tracking-tight uppercase mb-1">
                              {group.name}
                            </h3>
                            <p className="text-xs font-medium text-blue-700 flex items-center gap-1.5">
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              {group.max_choices === 1 ? 'Escolha até 1 item' : `Escolha até ${group.max_choices} itens`}
                            </p>
                          </div>
                        </div>
                        
                        {/* Badge de Obrigatório */}
                        {(() => {
                          const hasSelection = (groupSelections[group.id] || []).length > 0;
                          
                          if (group.is_required) {
                            return (
                              <div className="flex items-center flex-shrink-0 ml-2">
                                <span className={`px-3 py-1.5 text-xs font-bold rounded-full shadow-sm transition-all ${
                                  hasSelection 
                                    ? 'bg-green-500 text-white' 
                                    : 'bg-red-500 text-white animate-pulse'
                                }`}>
                                  {hasSelection ? '✓ Completo' : '⚠ Obrigatório'}
                                </span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>

                    {validationErrors[group.id] && (
                      <div className="px-4 py-3 bg-red-50 border-b border-red-100">
                        <p className="text-sm text-red-700 font-medium">
                          ⚠️ {validationErrors[group.id]}
                        </p>
                      </div>
                    )}

                    {/* Itens do Grupo - ESCOLHA ÚNICA (Radio) */}
                    {isSingleChoice ? (
                      <div className="divide-y divide-gray-100">
                        {group.additionals && group.additionals.map((additional) => {
                          const isSelected = (groupSelections[group.id] || []).includes(additional.id);
                          
                          return (
                            <button
                              key={additional.id}
                              onClick={() => updateAdditionalInGroup(group.id, additional, !isSelected)}
                              className={`w-full px-4 py-4 text-left transition-all ${
                                isSelected ? 'bg-primary-light' : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 text-base leading-snug">
                                    {additional.name}
                                  </p>
                                  {additional.description && (
                                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                                      {additional.description}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  {additional.price > 0 && (
                                    <p className="text-base font-bold text-gray-900">
                                      R$ {additional.price.toFixed(2)}
                                    </p>
                                  )}
                                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                    isSelected 
                                      ? 'border-primary bg-primary scale-110' 
                                      : 'border-gray-300 bg-white'
                                  }`}>
                                    {isSelected && (
                                      <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      /* Itens do Grupo - MÚLTIPLA ESCOLHA (Contador ou Checkbox se limit_one_per_item) */
                      <div className="divide-y divide-gray-100">
                        {group.additionals && group.additionals.map((additional) => {
                          const additionalKey = getAdditionalKey(group.id, additional.id);
                          const quantity = selectedAdditionals[additionalKey]?.quantity || 0;
                          const totalSelected = Object.values(selectedAdditionals)
                            .filter(add => add.groupId === group.id)
                            .reduce((sum, add) => sum + add.quantity, 0);
                          const isMaxReached = totalSelected >= group.max_choices && quantity === 0;
                          const limitOnePerItem = group.limit_one_per_item || false;
                          
                          return (
                            <div 
                              key={additional.id} 
                              className={`px-4 py-4 transition-colors ${
                                quantity > 0 ? 'bg-primary-light' : ''
                              }`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 text-base leading-snug">
                                    {additional.name}
                                  </p>
                                  {additional.description && (
                                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                                      {additional.description}
                                    </p>
                                  )}
                                  {additional.price > 0 && (
                                    <p className="text-base font-bold text-gray-900 mt-1.5">
                                      R$ {additional.price.toFixed(2)}
                                    </p>
                                  )}
                                </div>
                                
                                {/* Controles de Quantidade - Com suporte a limit_one_per_item */}
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  {limitOnePerItem ? (
                                    /* MODO CHECKBOX - limit_one_per_item = true */
                                    quantity > 0 ? (
                                      <button
                                        onClick={() => {
                                          const newAdditionals = { ...selectedAdditionals };
                                          delete newAdditionals[additionalKey];
                                          setSelectedAdditionals(newAdditionals);
                                          
                                          const newSelections = (groupSelections[group.id] || [])
                                            .filter(id => id !== additional.id);
                                          setGroupSelections({
                                            ...groupSelections,
                                            [group.id]: newSelections
                                          });
                                        }}
                                        className="w-8 h-8 flex items-center justify-center bg-green-500 hover:bg-green-600 transition-colors rounded-full shadow-md"
                                      >
                                        <Check size={18} className="text-white" strokeWidth={3} />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => {
                                          if (!isMaxReached) {
                                            setSelectedAdditionals({
                                              ...selectedAdditionals,
                                              [additionalKey]: { ...additional, quantity: 1, groupId: group.id }
                                            });
                                            
                                            const newSelections = [...(groupSelections[group.id] || []), additional.id];
                                            setGroupSelections({
                                              ...groupSelections,
                                              [group.id]: newSelections
                                            });
                                          }
                                        }}
                                        disabled={isMaxReached}
                                        className={`w-8 h-8 flex items-center justify-center transition-colors rounded-full shadow-sm ${
                                          isMaxReached 
                                            ? 'bg-gray-100 border-2 border-gray-200 cursor-not-allowed' 
                                            : 'bg-primary hover:bg-primary-hover'
                                        }`}
                                      >
                                        <Plus size={18} className={isMaxReached ? 'text-gray-400' : 'text-white'} />
                                      </button>
                                    )
                                  ) : (
                                    /* MODO CONTADOR - limit_one_per_item = false (comportamento padrão) */
                                    quantity > 0 ? (
                                    <>
                                      <button
                                        onClick={() => {
                                          const newQty = quantity - 1;
                                          if (newQty === 0) {
                                            const newAdditionals = { ...selectedAdditionals };
                                            delete newAdditionals[additionalKey];
                                            setSelectedAdditionals(newAdditionals);
                                            
                                            const newSelections = (groupSelections[group.id] || [])
                                              .filter(id => id !== additional.id);
                                            setGroupSelections({
                                              ...groupSelections,
                                              [group.id]: newSelections
                                            });
                                          } else {
                                            setSelectedAdditionals({
                                              ...selectedAdditionals,
                                              [additionalKey]: { ...additional, quantity: newQty, groupId: group.id }
                                            });
                                          }
                                        }}
                                        className="w-8 h-8 flex items-center justify-center bg-white border-2 border-gray-300 hover:border-gray-400 transition-colors rounded-full"
                                      >
                                        <Minus size={16} className="text-gray-700" />
                                      </button>
                                      
                                      <span className="w-8 text-center font-bold text-gray-900 text-lg">
                                        {quantity}
                                      </span>
                                      
                                      <button
                                        onClick={() => {
                                          const newQty = quantity + 1;
                                          // Calcular total de itens já selecionados no grupo (incluindo o incremento)
                                          const totalAfterIncrement = totalSelected + 1;
                                          
                                          if (totalAfterIncrement <= group.max_choices) {
                                            setSelectedAdditionals({
                                              ...selectedAdditionals,
                                              [additionalKey]: { ...additional, quantity: newQty, groupId: group.id }
                                            });
                                          } else {
                                            // Mostrar modal amigável em vez de alert
                                            setMaxLimitMessage({
                                              groupName: group.name,
                                              maxChoices: group.max_choices
                                            });
                                            setShowMaxLimitModal(true);
                                          }
                                        }}
                                        className="w-8 h-8 flex items-center justify-center bg-primary hover:bg-primary-hover transition-colors rounded-full shadow-md"
                                      >
                                        <Plus size={16} className="text-white" />
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        if (!isMaxReached) {
                                          setSelectedAdditionals({
                                            ...selectedAdditionals,
                                            [additionalKey]: { ...additional, quantity: 1, groupId: group.id }
                                          });
                                          
                                          const newSelections = [...(groupSelections[group.id] || []), additional.id];
                                          setGroupSelections({
                                            ...groupSelections,
                                            [group.id]: newSelections
                                          });
                                        }
                                      }}
                                      disabled={isMaxReached}
                                      className={`w-8 h-8 flex items-center justify-center transition-colors rounded-full shadow-sm ${
                                        isMaxReached 
                                          ? 'bg-gray-100 border-2 border-gray-200 cursor-not-allowed' 
                                          : 'bg-primary hover:bg-primary-hover'
                                      }`}
                                    >
                                      <Plus size={18} className={isMaxReached ? 'text-gray-400' : 'text-white'} />
                                    </button>
                                  )
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Campo de Observações */}
          <div className={`${productGroups.length > 0 ? 'mt-1' : 'mt-2'} mb-4`}>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Header - Fundo Destacado */}
              <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-300">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <h3 className="text-base font-bold text-gray-900">Observações</h3>
                  <span className="text-xs text-gray-500 ml-auto">(Opcional)</span>
                </div>
              </div>
              
              {/* Textarea */}
              <div className="p-4">
                <textarea
                  value={productNotes}
                  onChange={(e) => setProductNotes(e.target.value)}
                  placeholder="Ex.: Tirar cebola, ovo, etc."
                  rows={3}
                  maxLength={200}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
                />
                <div className="flex justify-end mt-2">
                  <span className="text-xs text-gray-500">
                    {productNotes.length}/200 caracteres
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Botão Fixo - Sempre Visível */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 shadow-lg z-30">
          <button 
            onClick={handleAddToCart}
            className="w-full bg-primary hover:bg-primary-hover text-white py-4 rounded-xl font-semibold text-base shadow-md transition-all duration-200 active:scale-95 animate-slow-pulse hover:animate-none hover:scale-105"
            data-testid="add-to-cart-button"
          >
            Avançar • R$ {getTotalPrice().toFixed(2)}
          </button>
        </div>

        {/* Modal de Validação - Design Compacto e Simpático com Portal */}
        {showValidationModal && ReactDOM.createPortal(
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: '16px',
              animation: 'overlayFadeIn 0.2s ease-out'
            }}
            onClick={handleCloseValidationModal}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                maxWidth: '350px',
                width: '100%',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                animation: 'modalFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                position: 'relative',
                zIndex: 100000,
                transformOrigin: 'center center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Ícone e Título - Mais compacto */}
              <div style={{ padding: '20px 20px 12px', textAlign: 'center' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  backgroundColor: '#fed7aa', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto 8px' 
                }}>
                  <svg style={{ width: '24px', height: '24px', color: '#f97316' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>
                  Ops! Falta algo...
                </h3>
                <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5' }}>
                  {validationMessage.message}
                </p>
              </div>
              
              {/* Botão compacto */}
              <div style={{ padding: '0 20px 20px' }}>
                <button
                  onClick={handleCloseValidationModal}
                  style={{
                    width: '100%',
                    backgroundColor: '#f97316',
                    color: 'white',
                    fontWeight: '600',
                    padding: '10px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#ea580c'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#f97316'}
                >
                  Levar-me até lá! 👆
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Modal de Limite Máximo - Design Simpático com Portal */}
        {showMaxLimitModal && ReactDOM.createPortal(
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: '16px',
              animation: 'overlayFadeIn 0.2s ease-out'
            }}
            onClick={() => setShowMaxLimitModal(false)}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                maxWidth: '350px',
                width: '100%',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                animation: 'modalFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                position: 'relative',
                zIndex: 100000,
                transformOrigin: 'center center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Ícone e Título */}
              <div style={{ padding: '20px 20px 12px', textAlign: 'center' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(to bottom right, #dbeafe, #bfdbfe)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px'
                }}>
                  <svg style={{ width: '24px', height: '24px', color: '#2563eb' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>
                  Limite Atingido! 🎯
                </h3>
                <div style={{ 
                  backgroundColor: '#eff6ff', 
                  borderRadius: '8px', 
                  padding: '12px', 
                  marginBottom: '8px' 
                }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#1e3a8a', marginBottom: '4px' }}>
                    {maxLimitMessage.groupName}
                  </p>
                  <p style={{ fontSize: '12px', color: '#1d4ed8' }}>
                    Máximo permitido: <span style={{ fontWeight: 'bold' }}>{maxLimitMessage.maxChoices} {maxLimitMessage.maxChoices === 1 ? 'item' : 'itens'}</span>
                  </p>
                </div>
                <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5' }}>
                  Você já escolheu o máximo permitido! 😊
                  <br />
                  <span style={{ fontWeight: '500', color: '#374151' }}>Remova algum item para adicionar outro.</span>
                </p>
              </div>
              
              {/* Botão */}
              <div style={{ padding: '0 20px 20px' }}>
                <button
                  onClick={() => setShowMaxLimitModal(false)}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(to right, #3b82f6, #2563eb)',
                    color: 'white',
                    fontWeight: '600',
                    padding: '10px',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = 'linear-gradient(to right, #2563eb, #1d4ed8)'}
                  onMouseOut={(e) => e.target.style.background = 'linear-gradient(to right, #3b82f6, #2563eb)'}
                >
                  Entendi! ✓
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

  // Cart Screen
  const CartScreen = () => (
    <div className="pt-20 pb-32 px-4 h-screen overflow-y-auto cart-scroll" style={{ WebkitOverflowScrolling: 'touch' }}>
      {cart.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Seu carrinho está vazio</p>
          <button 
            onClick={() => setCurrentScreen('home')}
            className="mt-4 bg-primary hover:bg-primary-hover text-white px-6 py-3 rounded-lg transition-colors"
          >
            Ver Cardápio
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-4 mb-6">
            {cart.map(item => (
              <div key={item.id} className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-start space-x-4">
                  {item.product.image_url && (
                    <img 
                      src={item.product.image_url} 
                      alt={item.product.name}
                      className="w-16 h-16 rounded-lg object-cover"
                      onError={(e) => {
                        e.target.src = "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg";
                      }}
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{item.quantity}x {item.product.name}</p>
                    {item.additionals.map(add => (
                      add.quantity > 0 && (
                        <p key={add.id} className="text-sm text-gray-600">
                          {add.quantity}x {add.name}
                        </p>
                      )
                    ))}
                    {item.notes && (
                      <p className="text-sm text-gray-600 italic mt-1">
                        📌 Obs: {item.notes}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center space-x-3">
                        <button 
                          onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center"
                        >
                          <Minus size={16} />
                        </button>
                        <span>{item.quantity}</span>
                        <button 
                          onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center"
                        >
                          <Plus size={16} />
                        </button>
                        <button 
                          onClick={() => updateCartItemQuantity(item.id, 0)}
                          className="ml-2 text-red-500"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <p className="font-bold">R$ {item.totalPrice.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button 
            onClick={() => setCurrentScreen('home')}
            className="w-full border border-primary text-primary py-3 rounded-lg mb-4 transition-all duration-300 hover:bg-primary-light"
          >
            Adicionar mais produtos
          </button>

          <div className="fixed bottom-4 left-4 right-4 z-50">
            <button 
              onClick={() => setCurrentScreen('checkout')}
              className="w-full bg-primary hover:bg-primary-hover text-white py-4 rounded-lg font-medium animate-slow-pulse hover:animate-none hover:scale-105 transition-all duration-300"
            >
              Avançar • R$ {getCartTotal().toFixed(2)}
            </button>
          </div>
        </>
      )}
    </div>
  );

  // Checkout Screen
  const CheckoutScreen = () => {
    const [checkoutErrors, setCheckoutErrors] = useState({});
    
    const subtotal = getCartTotal();
    // Taxa de entrega: modo radius usa calculatedFee, modo neighborhood usa selectedZone
    const deliveryFee = deliveryType === 'delivery' 
      ? (deliveryConfig.mode === 'radius' 
          ? (calculatedFee?.fee ?? 0) 
          : (selectedZone?.fee ?? 0))
      : 0;
    const cashbackDiscount = useCashback ? Math.min(cashbackAmount, subtotal + deliveryFee) : 0;
    const couponDiscountAmount = appliedCoupon ? couponDiscount : 0;
    const total = subtotal + deliveryFee - cashbackDiscount - couponDiscountAmount;
    const totalSavings = cashbackDiscount + couponDiscountAmount;

    // Auto-load cashback when entering checkout
    React.useEffect(() => {
      if (customerPhone && !cashbackData && restaurant) {
        loadCashbackData(customerPhone);
      }
    }, [customerPhone, cashbackData, restaurant]);

    // 📅 Show scheduling modal when store is closed and scheduling is enabled
    // Uses ref to track if already shown to prevent flickering
    const schedulingCheckedRef = React.useRef(false);
    
    React.useEffect(() => {
      // Prevent multiple executions
      if (schedulingCheckedRef.current || isScheduledOrder || schedulingModalShown) {
        return;
      }
      
      const checkScheduling = async () => {
        const isStoreClosed = restaurant?.status?.is_auto && !restaurant?.status?.is_open;
        const schedulingEnabled = restaurant?.scheduling?.enabled;
        const schedulingAvailable = restaurant?.scheduling?.available;
        
        // Show modal if: store is closed, scheduling is enabled, and not already scheduled
        if (isStoreClosed && schedulingEnabled && schedulingAvailable) {
          schedulingCheckedRef.current = true;
          const slotsData = await loadSchedulingSlots();
          if (slotsData.scheduling_available && slotsData.slots?.length > 0) {
            setSchedulingModalShown(true);
            setShowSchedulingModal(true);
          }
        }
      };
      
      // Run only once when checkout screen mounts
      checkScheduling();
    }, [restaurant, isScheduledOrder, schedulingModalShown]); // Dependencies to control re-runs

    const StepIndicator = () => (
      <div className="flex items-center justify-center space-x-4 mb-6">
        {[1, 2, 3, 4].map(step => (
          <React.Fragment key={step}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              checkoutStep >= step ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              {step}
            </div>
            {step < 4 && (
              <div className={`w-8 h-0.5 ${checkoutStep > step ? 'bg-primary' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );

    const validateStep = () => {
      const errors = {};
      
      if (checkoutStep === 1) {
        // Validar nome e telefone
        const name = customerNameRef.current?.value || customerName;
        const phone = customerPhoneRef.current?.value || customerPhone;
        
        if (!name || name.trim() === '') {
          errors.customerName = 'Por favor, informe seu nome';
        }
        if (!phone || phone.trim() === '' || phone.length < 10) {
          errors.customerPhone = 'Por favor, informe um telefone válido';
        }
      } else if (checkoutStep === 3 && deliveryType === 'delivery') {
        // Validar endereço
        const street = streetRef.current?.value || address.street;
        const number = numberRef.current?.value || address.number;
        
        if (!street || street.trim() === '') {
          errors.street = 'Por favor, informe a rua';
        }
        if (!number || number.trim() === '') {
          errors.number = 'Por favor, informe o número';
        }
        
        // ===== VALIDAÇÃO OBRIGATÓRIA DO BAIRRO =====
        if (!address.neighborhood || address.neighborhood.trim() === '') {
          errors.neighborhood = 'Por favor, informe o bairro';
        }
        
        // Validação específica por modo de entrega
        if (deliveryConfig.mode === 'neighborhood') {
          // Modo bairro: precisa ter zonas cadastradas e bairro válido
          if (deliveryZones.length === 0) {
            errors.neighborhood = 'Não há bairros disponíveis para entrega';
          }
          
          if (address.neighborhood && deliveryZones.length > 0) {
            const validZone = deliveryZones.find(z => z.name === address.neighborhood);
            if (!validZone) {
              errors.neighborhood = 'Bairro selecionado não é válido';
            }
          }
        } else if (deliveryConfig.mode === 'radius') {
          // Modo raio: precisa ter calculado a taxa
          if (!calculatedFee) {
            errors.neighborhood = 'Clique em "Calcular Taxa de Entrega" para verificar a taxa';
          }
        }
      }
      
      setCheckoutErrors(errors);
      
      // Se houver erros, mostrar toast
      if (Object.keys(errors).length > 0 && errors.neighborhood) {
        toast.error(errors.neighborhood);
      }
      
      return Object.keys(errors).length === 0;
    };

    const handleContinue = async () => {
      // Check if store is suspended/expired before allowing to proceed
      const isSuspended = restaurant?.status?.is_suspended === true;
      const isExpired = restaurant?.status?.is_expired === true;
      const isStoreClosed = restaurant?.status?.is_auto && !restaurant.status.is_open;
      const schedulingEnabled = restaurant?.scheduling?.enabled;
      
      // If store is closed and scheduling is enabled, check for scheduling
      if (isStoreClosed && schedulingEnabled && !isScheduledOrder && !schedulingModalShown && checkoutStep === 4) {
        const slotsData = await loadSchedulingSlots();
        if (slotsData.scheduling_available) {
          setSchedulingModalShown(true);
          setShowSchedulingModal(true);
          return;
        }
      }
      
      if ((isSuspended || isExpired || (isStoreClosed && !isScheduledOrder && !schedulingEnabled)) && checkoutStep === 4) {
        setShowClosedStoreModal(true);
        return;
      }
      
      // Validar valor mínimo quando está na etapa 2 e escolheu delivery
      if (checkoutStep === 2 && deliveryType === 'delivery') {
        const minimumOrder = restaurant?.minimum_order || 0;
        const cartTotal = getCartTotal();
        
        if (minimumOrder > 0 && cartTotal < minimumOrder) {
          setShowMinOrderModal(true);
          return;
        }
      }
      
      if (validateStep()) {
        if (checkoutStep < 4) {
          // Se estiver na etapa 2 (tipo de entrega) e escolheu "pickup" (retirada no local)
          // pula direto para a etapa 4 (forma de pagamento)
          if (checkoutStep === 2 && deliveryType === 'pickup') {
            setCheckoutStep(4);
          } else {
            setCheckoutStep(checkoutStep + 1);
          }
          setCheckoutErrors({}); // Limpar erros ao avançar
        } else {
          handleWhatsAppOrder();
        }
      } else {
        // Scroll para o primeiro campo com erro
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    const copyPixKey = () => {
      // Usar a chave PIX cadastrada pelo lojista ou mostrar mensagem de erro
      const pixKey = restaurant?.payment_methods?.pix_key;
      
      if (!pixKey) {
        alert('Chave PIX não configurada pelo estabelecimento. Entre em contato com o restaurante.');
        return;
      }
      
      navigator.clipboard.writeText(pixKey).then(() => {
        setPixCopied(true);
        setTimeout(() => setPixCopied(false), 4000); // Reset após 4 segundos
      }).catch(err => {
        console.error('Erro ao copiar:', err);
        alert('Chave PIX: ' + pixKey);
      });
    };

    return (
      <div className="fixed inset-0 bg-gray-50 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="pt-20 pb-32 px-4">
        <StepIndicator />

        {checkoutStep === 1 && (
          <div>
            <h3 className="text-lg font-bold mb-4">Seus Dados</h3>
            <div className="space-y-4">
              <div>
                <input 
                  ref={customerNameRef}
                  type="text"
                  placeholder="Seu nome completo"
                  defaultValue={customerName}
                  onBlur={(e) => {
                    setCustomerName(e.target.value);
                    saveCustomerData(e.target.value, customerPhoneInput);
                  }}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    checkoutErrors.customerName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  data-testid="customer-name-input"
                />
                {checkoutErrors.customerName && (
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-yellow-500">⚠️</span>
                    <p className="text-sm text-red-600">{checkoutErrors.customerName}</p>
                  </div>
                )}
              </div>
              <div>
                <input 
                  ref={customerPhoneRef}
                  type="tel"
                  placeholder="(00) 00000-0000"
                  defaultValue={customerPhoneInput}
                  onBlur={(e) => {
                    setCustomerPhoneInput(e.target.value);
                    setCustomerPhone(e.target.value);
                    saveCustomerData(customerName, e.target.value);
                  }}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    checkoutErrors.customerPhone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  data-testid="customer-phone-input"
                />
                {checkoutErrors.customerPhone && (
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-yellow-500">⚠️</span>
                    <p className="text-sm text-red-600">{checkoutErrors.customerPhone}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {checkoutStep === 2 && (
          <div>
            <h3 className="text-lg font-bold mb-4">Tipo de Entrega</h3>
            <div className="space-y-4">
              <button
                onClick={() => setDeliveryType('delivery')}
                className={`w-full p-4 rounded-lg border-2 flex items-center space-x-3 transition-all duration-300 ${
                  deliveryType === 'delivery' ? 'border-primary bg-primary-light' : 'border-gray-200'
                }`}
              >
                <Truck size={24} className={deliveryType === 'delivery' ? 'text-primary' : 'text-gray-600'} />
                <span className={`font-medium ${deliveryType === 'delivery' ? 'text-primary' : 'text-gray-600'}`}>
                  Delivery
                </span>
              </button>
              <button
                onClick={() => setDeliveryType('pickup')}
                className={`w-full p-4 rounded-lg border-2 flex items-center space-x-3 transition-all duration-300 ${
                  deliveryType === 'pickup' ? 'border-primary bg-primary-light' : 'border-gray-200'
                }`}
              >
                <Store size={24} className={deliveryType === 'pickup' ? 'text-primary' : 'text-gray-600'} />
                <span className={`font-medium ${deliveryType === 'pickup' ? 'text-primary' : 'text-gray-600'}`}>
                  Retirada no Local
                </span>
              </button>
            </div>
          </div>
        )}

        {checkoutStep === 3 && deliveryType === 'delivery' && (
          <div>
            <h3 className="text-lg font-bold mb-4">Endereço de Entrega</h3>
            <div className="space-y-4">
              
              {/* MODO NEIGHBORHOOD: Seleção de bairro com taxa fixa */}
              {deliveryConfig.mode === 'neighborhood' && (
                <NeighborhoodSelector
                  zones={deliveryZones}
                  selectedZone={selectedZone}
                  onSelect={(zone) => {
                    const zoneName = zone?.name || '';
                    const newAddress = {...address, neighborhood: zoneName};
                    setAddress(newAddress);
                    setSelectedZone(zone);
                    saveAddressData({ neighborhood: zoneName });
                  }}
                  error={checkoutErrors.neighborhood}
                />
              )}
              
              {/* MODO RADIUS: Campo CEP + Entrada de bairro manual + cálculo de taxa por distância */}
              {deliveryConfig.mode === 'radius' && (
                <>
                  {/* Campo CEP com busca automática */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">CEP</label>
                    <div className="relative">
                      <input 
                        ref={cepRef}
                        type="text"
                        inputMode="numeric"
                        maxLength={9}
                        defaultValue={address.cep}
                        onChange={(e) => {
                          // Formatar CEP: 00000-000
                          let value = e.target.value.replace(/\D/g, '');
                          if (value.length > 5) {
                            value = value.slice(0, 5) + '-' + value.slice(5, 8);
                          }
                          e.target.value = value;
                          
                          // Buscar automaticamente quando tiver 8 dígitos
                          if (value.replace(/\D/g, '').length === 8) {
                            fetchAddressByCep(value);
                          }
                        }}
                        onBlur={(e) => {
                          const cepValue = e.target.value.replace(/\D/g, '');
                          setAddress(prev => ({...prev, cep: cepValue}));
                          saveAddressData({ cep: cepValue });
                        }}
                        placeholder="00000-000"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                      {loadingCep && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <svg className="animate-spin h-5 w-5 text-primary" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Digite o CEP para preencher o endereço automaticamente</p>
                  </div>

                  {/* Campo Bairro */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bairro</label>
                    <input 
                      ref={neighborhoodRef}
                      type="text"
                      defaultValue={address.neighborhood}
                      onBlur={(e) => {
                        const newNeighborhood = e.target.value;
                        setAddress(prev => ({...prev, neighborhood: newNeighborhood}));
                        saveAddressData({ neighborhood: newNeighborhood });
                      }}
                      placeholder="Digite o nome do seu bairro"
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                        checkoutErrors.neighborhood ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {checkoutErrors.neighborhood && (
                      <div className="flex items-center gap-1 mt-2">
                        <span className="text-yellow-500">⚠️</span>
                        <p className="text-sm text-red-600">{checkoutErrors.neighborhood}</p>
                      </div>
                    )}
                  </div>
                </>
              )}
              
              {/* Campos comuns: Rua, Número, Complemento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rua</label>
                <input 
                  ref={streetRef}
                  type="text"
                  defaultValue={address.street}
                  onBlur={(e) => {
                    const newStreet = e.target.value;
                    setAddress(prev => ({...prev, street: newStreet}));
                    saveAddressData({ street: newStreet });
                  }}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    checkoutErrors.street ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {checkoutErrors.street && (
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-yellow-500">⚠️</span>
                    <p className="text-sm text-red-600">{checkoutErrors.street}</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Número</label>
                  <input 
                    ref={numberRef}
                    type="text"
                    defaultValue={address.number}
                    onBlur={(e) => {
                      const newNumber = e.target.value;
                      setAddress(prev => ({...prev, number: newNumber}));
                      saveAddressData({ number: newNumber });
                    }}
                    className={`w-full p-3 border rounded-lg ${
                      checkoutErrors.number ? 'border-red-500' : 'border-gray-300 bg-gray-100'
                    }`}
                  />
                  {checkoutErrors.number && (
                    <div className="flex items-center gap-1 mt-2">
                      <span className="text-yellow-500">⚠️</span>
                      <p className="text-sm text-red-600">{checkoutErrors.number}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Complemento</label>
                  <input 
                    ref={complementRef}
                    type="text"
                    defaultValue={address.complement}
                    onBlur={(e) => {
                      const newComplement = e.target.value;
                      setAddress(prev => ({...prev, complement: newComplement}));
                      saveAddressData({ complement: newComplement });
                    }}
                    className="w-full p-3 bg-gray-100 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              
              {/* MODO RADIUS: Indicador de cálculo automático de taxa */}
              {deliveryConfig.mode === 'radius' && (
                <div className="mt-4">
                  {/* Indicador de cálculo em andamento */}
                  {calculatingFee && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        <p className="text-sm text-blue-700">Calculando taxa de entrega...</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Resultado do cálculo automático */}
                  {calculatedFee && !calculatingFee && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600 text-lg">✓</span>
                        <div>
                          <p className="text-sm font-medium text-green-800">
                            Distância: {calculatedFee.distance} km
                          </p>
                          <p className="text-lg font-bold text-green-700">
                            Taxa de entrega: R$ {calculatedFee.fee.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Erro no cálculo */}
                  {feeError && !calculatingFee && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-red-500 text-lg">⚠️</span>
                        <p className="text-sm text-red-700">{feeError}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Mensagem para preencher endereço */}
                  {!calculatedFee && !calculatingFee && !feeError && (!address.street || !address.number || !address.neighborhood) && (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-600">
                        Preencha o endereço completo para calcular a taxa de entrega automaticamente.
                      </p>
                    </div>
                  )}
                  
                  {/* Botão para recalcular (caso queira forçar) */}
                  {(feeError || (!calculatedFee && address.street && address.number && address.neighborhood)) && !calculatingFee && (
                    <button
                      type="button"
                      onClick={() => {
                        lastCalculatedAddressRef.current = ''; // Limpar para permitir recálculo
                        calculateDeliveryFeeByRadius(true);
                      }}
                      className="mt-2 w-full p-2 text-sm rounded-lg font-medium bg-primary text-white hover:bg-primary-hover transition-all duration-200"
                    >
                      Recalcular Taxa
                    </button>
                  )}
                  
                  {/* Faixas de preço disponíveis */}
                  {deliveryConfig.radius_zones && deliveryConfig.radius_zones.length > 0 && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-600 mb-2">Faixas de entrega:</p>
                      <div className="space-y-1">
                        {deliveryConfig.radius_zones.map((zone, idx) => (
                          <p key={idx} className="text-xs text-gray-500">
                            {zone.min_distance} - {zone.max_distance} km: R$ {zone.fee.toFixed(2)}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {checkoutStep === 4 && (
          <div>
            <h3 className="text-lg font-bold mb-4">Pagamento</h3>
            
            {/* Scheduled Order Indicator */}
            {isScheduledOrder && selectedScheduleDate && selectedScheduleTime && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                    <Clock className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900">📅 Pedido Agendado</p>
                    <p className="text-base font-bold text-blue-700">
                      {schedulingSlots.find(s => s.date === selectedScheduleDate)?.is_today ? 'Hoje' : 
                       schedulingSlots.find(s => s.date === selectedScheduleDate)?.is_tomorrow ? 'Amanhã' :
                       schedulingSlots.find(s => s.date === selectedScheduleDate)?.day_name} às {selectedScheduleTime}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      await loadSchedulingSlots();
                      setShowSchedulingModal(true);
                    }}
                    className="text-blue-600 text-sm font-medium hover:text-blue-800"
                  >
                    Alterar
                  </button>
                </div>
              </div>
            )}
            
            {/* Cashback Button - Only show if has balance */}
            {cashbackData && 
             cashbackData.enabled === true && 
             typeof cashbackData.available_balance === 'number' &&
             cashbackData.available_balance > 0 && (
              <div className="mb-6">
                {!useCashback ? (
                  <button
                    onClick={() => {
                      const maxCashback = Math.min(
                        cashbackData.available_balance || 0, 
                        subtotal + deliveryFee
                      );
                      setUseCashback(true);
                      setCashbackAmount(maxCashback);
                    }}
                    className="w-full p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-xl hover:from-amber-100 hover:to-yellow-100 transition-all duration-300 shadow-sm hover:shadow-md"
                    data-testid="activate-cashback-button"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-md">
                          <Star className="text-white" size={24} fill="white" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-semibold text-amber-900">Você tem Cashback!</p>
                          <p className="text-lg font-bold text-amber-700">
                            R$ {(cashbackData.available_balance || 0).toFixed(2)} disponível
                          </p>
                        </div>
                      </div>
                      <div className="text-amber-600">
                        <ChevronRight size={24} />
                      </div>
                    </div>
                    <p className="text-xs text-amber-700 mt-2 text-center">
                      👆 Toque para usar seu cashback neste pedido
                    </p>
                  </button>
                ) : (
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-md animate-pulse">
                          <CheckCircle className="text-white" size={28} fill="white" />
                        </div>
                        <div className="text-left">
                          <p className="text-base font-bold text-green-900">✨ Cashback Ativado!</p>
                          <p className="text-sm text-green-700">
                            Desconto de R$ {cashbackAmount.toFixed(2)} aplicado
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            {cashbackAmount < cashbackData.available_balance 
                              ? `Você ainda tem R$ ${(cashbackData.available_balance - cashbackAmount).toFixed(2)} disponível`
                              : 'Saldo total utilizado'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setUseCashback(false);
                          setCashbackAmount(0);
                        }}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors shadow-sm"
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Payment Methods */}
            <div className="space-y-4 mb-6">
              {['dinheiro', 'cartao', 'pix', 'pix_mp', 'vale_refeicao', 'vale_alimentacao']
                .filter(method => restaurant?.payment_methods?.[method])
                .map(method => {
                  // Função para obter as bandeiras formatadas
                  const getBandeirasLabel = (tipo) => {
                    const bandeirasMap = {
                      visa: 'Visa', mastercard: 'Master', elo: 'Elo', 
                      amex: 'Amex', hipercard: 'Hiper', diners: 'Diners',
                      sodexo: 'Sodexo', alelo: 'Alelo', ticket: 'Ticket',
                      vr: 'VR', ben: 'Ben', greencard: 'Green Card', cooper: 'Cooper'
                    };
                    const bandeiras = restaurant?.payment_methods?.[tipo] || [];
                    if (!bandeiras || bandeiras.length === 0) return null;
                    return bandeiras.map(b => bandeirasMap[b] || b).join(' • ');
                  };

                  const bandeirasCartao = method === 'cartao' ? getBandeirasLabel('cartao_bandeiras') : null;
                  const bandeirasValeRefeicao = method === 'vale_refeicao' ? getBandeirasLabel('vale_refeicao_bandeiras') : null;
                  const bandeirasValeAlimentacao = method === 'vale_alimentacao' ? getBandeirasLabel('vale_alimentacao_bandeiras') : null;
                  const bandeiras = bandeirasCartao || bandeirasValeRefeicao || bandeirasValeAlimentacao;

                  return (
                <button
                  key={method}
                  onClick={() => {
                    setPaymentMethod(method);
                    // Reset states when changing payment method
                    if (method !== 'dinheiro') {
                      setNeedsChange(false);
                      setChangeAmount('');
                    }
                    if (method !== 'pix' && method !== 'pix_mp') {
                      setPixCopied(false);
                    }
                  }}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-300 ${
                    paymentMethod === method ? 'border-primary bg-primary-light' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${paymentMethod === method ? 'text-primary' : 'text-gray-600'}`}>
                      {method === 'dinheiro' ? '💵 Dinheiro' : 
                       method === 'cartao' ? '💳 Cartão' : 
                       method === 'pix' ? '🔑 PIX (Chave Manual)' : 
                       method === 'pix_mp' ? '🚀 PIX Automático (Mercado Pago)' :
                       method === 'vale_refeicao' ? '🍽️ Vale Refeição' :
                       '🛒 Vale Alimentação'}
                    </span>
                    {method === 'pix_mp' && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold">
                        Instantâneo
                      </span>
                    )}
                  </div>
                  {/* Bandeiras aceitas - exibição discreta */}
                  {bandeiras && (
                    <p className="text-xs text-gray-400 mt-1">
                      {bandeiras}
                    </p>
                  )}
                  {method === 'pix_mp' && (
                    <p className="text-xs text-gray-500 mt-1">
                      QR Code gerado automaticamente - Confirmação instantânea
                    </p>
                  )}
                </button>
                  );
              })}
            </div>

            {/* Campo de Troco - Compacto e harmonioso */}
            {paymentMethod === 'dinheiro' && (
              <div className="mb-4 bg-white border border-gray-200 rounded-lg p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={needsChange}
                    onChange={(e) => {
                      setNeedsChange(e.target.checked);
                      if (!e.target.checked) setChangeAmount('');
                    }}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    💵 Precisa de troco?
                  </span>
                </label>
                
                {needsChange && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <label className="block text-xs text-gray-600 mb-1.5">
                      Troco para quanto?
                    </label>
                    <input
                      ref={changeAmountRef}
                      type="number"
                      step="0.01"
                      defaultValue={changeAmount}
                      onChange={(e) => setChangeAmount(e.target.value)}
                      placeholder="Ex: 50.00"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      autoFocus
                    />
                  </div>
                )}
              </div>
            )}

            {/* Botão de Copiar PIX - Aparece ABAIXO das opções quando seleciona PIX */}
            {paymentMethod === 'pix' && (
              <div className="mb-6 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">📱</span>
                  <p className="text-sm text-gray-700 font-medium">
                    Chave PIX para pagamento:
                  </p>
                </div>
                
                {restaurant?.payment_methods?.pix_key ? (
                  <>
                    {pixCopied ? (
                      // Estado: Chave Copiada (Verde)
                      <button className="w-full bg-green-500 text-white rounded-lg p-4 flex items-center justify-center gap-2 font-semibold text-base transition-all">
                        <CheckCircle size={24} />
                        Chave copiada!
                      </button>
                    ) : (
                      // Estado: Normal (Branco com chave)
                      <button
                        onClick={copyPixKey}
                        className="w-full bg-white rounded-lg border border-green-200 p-3 flex items-center justify-between hover:border-green-300 transition-colors"
                      >
                        <span className="text-gray-900 font-medium">{restaurant.payment_methods.pix_key}</span>
                        <Copy size={20} className="text-gray-600" />
                      </button>
                    )}
                    
                    <div className="flex items-center gap-1 mt-2 justify-center">
                      <span className="text-lg">👉</span>
                      <p className="text-xs text-gray-600">
                        {pixCopied ? 'Chave copiada!' : 'Toque para copiar a chave'}
                      </p>
                    </div>
                  </>
                ) : (
                  // Chave PIX não configurada
                  <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3 text-center">
                    <p className="text-yellow-800 text-sm font-medium">
                      ⚠️ Chave PIX não configurada
                    </p>
                    <p className="text-yellow-700 text-xs mt-1">
                      Entre em contato com o estabelecimento para obter a chave PIX.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* 🎟️ Coupon Section - Compact version with checkbox */}
            {hasActiveCoupons && !appliedCoupon && (
              <div className="mb-4 bg-white border border-gray-200 rounded-lg p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showCouponInput}
                    onChange={(e) => {
                      setShowCouponInput(e.target.checked);
                      if (!e.target.checked) {
                        setCouponCode('');
                        setCouponError('');
                      }
                    }}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    🎟️ Tenho um cupom de desconto
                  </span>
                </label>
                
                {showCouponInput && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && couponCode.trim()) {
                            validateCoupon();
                          }
                        }}
                        placeholder="Digite o código"
                        className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 uppercase focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                        maxLength={30}
                        disabled={couponLoading}
                        autoFocus
                      />
                      <button
                        onClick={validateCoupon}
                        disabled={!couponCode.trim() || couponLoading}
                        className="px-4 py-2 text-sm bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {couponLoading ? (
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span className="hidden sm:inline text-xs">Validando</span>
                          </div>
                        ) : (
                          'Aplicar'
                        )}
                      </button>
                    </div>
                    
                    {couponError && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-red-600 text-xs">{couponError}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Applied Coupon Display */}
            {appliedCoupon && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <p className="text-green-700 font-bold text-sm">{appliedCoupon.code}</p>
                    </div>
                    <p className="text-green-600 text-xs">
                      Desconto de <span className="font-bold">R$ {couponDiscount.toFixed(2)}</span> aplicado! 🎉
                    </p>
                    {appliedCoupon.description && (
                      <p className="text-gray-600 text-xs mt-1">{appliedCoupon.description}</p>
                    )}
                  </div>
                  <button
                    onClick={removeCoupon}
                    className="text-gray-400 hover:text-red-500 transition-colors p-1"
                    title="Remover cupom"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Order Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>R$ {subtotal.toFixed(2)}</span>
              </div>
              {deliveryType === 'delivery' && (
                <div className="flex justify-between">
                  <span>Taxa de entrega</span>
                  <span>R$ {deliveryFee.toFixed(2)}</span>
                </div>
              )}
              {useCashback && cashbackDiscount > 0 && (
                <div className="flex justify-between text-green-600 font-semibold">
                  <span>💰 Cashback aplicado</span>
                  <span>- R$ {cashbackDiscount.toFixed(2)}</span>
                </div>
              )}
              {appliedCoupon && couponDiscountAmount > 0 && (
                <div className="flex justify-between text-purple-600 font-semibold">
                  <div className="flex items-center gap-1">
                    <span className="text-base">🎟️</span>
                    <span>Cupom {appliedCoupon.code}</span>
                  </div>
                  <span>- R$ {couponDiscountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total</span>
                <span data-testid="final-total">R$ {total.toFixed(2)}</span>
              </div>
              
              {/* Gamificação - Economia Total */}
              {totalSavings > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center mt-2">
                  <p className="text-green-700 font-bold text-sm flex items-center justify-center gap-2">
                    <span className="text-xl">💚</span>
                    Você está economizando R$ {totalSavings.toFixed(2)}!
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="fixed bottom-4 left-4 right-4 z-50 pb-safe">
          <button 
            onClick={handleContinue}
            disabled={
              // Desabilitar se está enviando pedido
              isSubmittingOrder ||
              // Desabilitar se loja fechada/suspensa no step 4
              (checkoutStep === 4 && !isScheduledOrder && (restaurant?.status?.is_suspended || restaurant?.status?.is_expired || (restaurant?.status?.is_auto && !restaurant.status.is_open))) ||
              // Desabilitar se step 3 (endereço) e bairro não selecionado em delivery
              (checkoutStep === 3 && deliveryType === 'delivery' && (!address.neighborhood || address.neighborhood.trim() === '' || deliveryZones.length === 0))
            }
            className={`w-full py-4 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
              isSubmittingOrder ||
              (checkoutStep === 4 && !isScheduledOrder && (restaurant?.status?.is_suspended || restaurant?.status?.is_expired || (restaurant?.status?.is_auto && !restaurant.status.is_open))) ||
              (checkoutStep === 3 && deliveryType === 'delivery' && (!address.neighborhood || address.neighborhood.trim() === '' || deliveryZones.length === 0))
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-primary hover:bg-primary-hover text-white animate-slow-pulse hover:animate-none hover:scale-105'
            }`}
            data-testid="checkout-continue-btn"
          >
            {isSubmittingOrder ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enviando...
              </>
            ) : (
              checkoutStep < 4 ? 'Continuar' : (isScheduledOrder ? '📅 Finalizar Pedido Agendado' : 'Finalizar Pedido')
            )}
          </button>
        </div>
        </div>
      </div>
    );
  };

  // Cart Confirmation Modal
  const CartConfirmationModal = () => {
    if (!showCartConfirmation) return null;

    return (
      <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={() => setShowCartConfirmation(false)}
        />
        
        {/* Modal Content */}
        <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 shadow-2xl animate-slide-up">
          <div className="text-center">
            {/* Success Icon - Carrinho com animação de pulo único + pulso contínuo */}
            <div 
              className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-4 shadow-lg animate-cart-bounce-once"
              style={{
                background: `linear-gradient(to bottom right, var(--color-primary-light, #EFF6FF), var(--color-primary-light, #EFF6FF))`
              }}
            >
              <ShoppingCart 
                className="w-10 h-10 animate-cart-pulse" 
                style={{ color: 'var(--color-primary, #3B82F6)' }}
              />
            </div>
            
            {/* Message */}
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Produto Adicionado! ✓
            </h3>
            <p className="mb-6 font-medium" style={{ color: 'var(--color-primary, #3B82F6)' }}>
              Item adicionado ao carrinho com sucesso
            </p>
            
            {/* Buttons - ORDEM INVERTIDA */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowCartConfirmation(false);
                  setCurrentScreen('home');
                }}
                className="w-full bg-white text-primary py-3 px-4 rounded-lg font-semibold hover:bg-primary-light transition-all duration-200 border-2 border-primary shadow-sm hover:shadow-md"
              >
                Continuar Comprando
              </button>
              
              <button
                onClick={() => {
                  setShowCartConfirmation(false);
                  setCurrentScreen('cart');
                }}
                className="w-full text-white py-3 px-4 rounded-lg font-bold transition-all duration-200 animate-slow-pulse hover:animate-none"
                style={{
                  background: `linear-gradient(to right, var(--color-primary, #3B82F6), var(--color-primary-hover, #2563EB))`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `linear-gradient(to right, var(--color-primary-hover, #2563EB), var(--color-primary-hover, #2563EB))`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `linear-gradient(to right, var(--color-primary, #3B82F6), var(--color-primary-hover, #2563EB))`;
                }}
              >
                Ir para o Carrinho
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Closed Store Modal Component
  const ClosedStoreModal = () => {
    if (!showClosedStoreModal) return null;

    const nextOpeningInfo = getNextOpeningInfo();
    const isExpired = restaurant?.status?.is_expired === true;
    const isSuspended = restaurant?.status?.is_suspended === true;

    return (
      <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
          onClick={() => setShowClosedStoreModal(false)}
        />
        
        {/* Modal Content */}
        <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md shadow-2xl animate-slide-up">
          {/* Header com gradiente */}
          <div className={`p-6 rounded-t-3xl sm:rounded-t-2xl ${
            isExpired || isSuspended 
              ? 'bg-gradient-to-br from-red-500 to-orange-500' 
              : 'bg-gradient-to-br from-orange-500 to-red-500'
          }`}>
            <div className="text-center">
              {/* Icon */}
              <div className="mx-auto w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-4 backdrop-blur-sm">
                <Store className="w-10 h-10 text-white" />
              </div>
              
              {/* Title */}
              <h3 className="text-2xl font-bold text-white mb-2">
                {isExpired || isSuspended ? '🔒 Loja Temporariamente Indisponível' : 'Ops! Estamos Fechados'}
              </h3>
              <p className="text-white text-opacity-90 text-sm">
                {isExpired || isSuspended 
                  ? 'Esta loja está temporariamente desativada'
                  : 'No momento não estamos aceitando pedidos'}
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {/* Current Status */}
            <div className={`border-2 rounded-xl p-4 ${
              isExpired || isSuspended 
                ? 'bg-red-50 border-red-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                </div>
                <div className="flex-1">
                  <p className="text-red-800 font-semibold text-sm mb-1">
                    Status Atual
                  </p>
                  <p className="text-red-700 text-sm">
                    {isExpired || isSuspended 
                      ? 'Estabelecimento temporariamente fechado. Você pode visualizar o cardápio, mas não é possível fazer pedidos no momento.'
                      : restaurant?.status?.message || 'Estabelecimento fechado'}
                  </p>
                </div>
              </div>
            </div>

            {/* Next Opening - Only show if not expired/suspended */}
            {!isExpired && !isSuspended && nextOpeningInfo && (
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-green-800 font-semibold text-sm mb-2">
                      Próxima Abertura
                    </p>
                    <div className="space-y-1">
                      <p className="text-green-700 text-base font-bold">
                        {nextOpeningInfo.day === 'hoje' ? 'Hoje' : nextOpeningInfo.day === 'amanhã' ? 'Amanhã' : nextOpeningInfo.fullDay}
                      </p>
                      <p className="text-green-600 text-sm">
                        às {nextOpeningInfo.time}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Opening Hours Schedule - Only show if not expired/suspended */}
            {!isExpired && !isSuspended && restaurant?.status?.schedule && (
              <div className="border-t border-gray-200 pt-4">
                <button
                  onClick={() => setShowStoreProfile(true)}
                  className="w-full flex items-center justify-between text-left py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-light rounded-full flex items-center justify-center group-hover:bg-primary-light transition-colors">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        Ver Horário de Funcionamento
                      </p>
                      <p className="text-xs text-gray-500">
                        Confira todos os nossos horários
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </button>
              </div>
            )}

            {/* Action Button */}
            <button
              onClick={() => {
                setShowClosedStoreModal(false);
                setCurrentScreen('home');
              }}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95 flex items-center justify-center space-x-2"
              style={{ minHeight: '52px' }}
            >
              <Home className="w-5 h-5" />
              <span>Voltar ao Cardápio</span>
            </button>

            {/* Info Footer */}
            <p className="text-center text-xs text-gray-500">
              💡 Você pode explorar nosso cardápio enquanto aguarda
            </p>
          </div>
        </div>
      </div>
    );
  };

  // 📅 Scheduling Modal Component
  const SchedulingModal = () => {
    if (!showSchedulingModal) return null;

    const handleConfirmScheduling = () => {
      if (!selectedScheduleDate || !selectedScheduleTime) {
        return;
      }
      setIsScheduledOrder(true);
      setShowSchedulingModal(false);
      // Continue to checkout - keep schedulingModalShown true to prevent re-show
      setCurrentScreen('checkout');
    };

    const handleCancelScheduling = () => {
      setSelectedScheduleDate(null);
      setSelectedScheduleTime(null);
      setIsScheduledOrder(false);
      setShowSchedulingModal(false);
      setSchedulingModalShown(false); // Reset to allow re-show if user tries again
      setCurrentScreen('home');
    };

    // Get selected slot info
    const selectedSlot = schedulingSlots.find(s => s.date === selectedScheduleDate);

    return (
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleCancelScheduling}
        />
        
        {/* Modal Content - Otimizado para mobile */}
        <div 
          className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl animate-slide-up flex flex-col"
          style={{ maxHeight: 'calc(100vh - 40px)', maxHeight: 'calc(100dvh - 40px)' }}
        >
          {/* Header Compacto */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-4 rounded-t-2xl sm:rounded-t-2xl flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-white">
                  Loja fechada agora
                </h3>
                <p className="text-white/90 text-sm">
                  Agende seu pedido para depois! 📅
                </p>
              </div>
              <button 
                onClick={handleCancelScheduling}
                className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Body - Scrollable */}
          <div className="flex-1 overflow-y-auto overscroll-contain p-4 min-h-0">
            {loadingSlots ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
              </div>
            ) : (
              <>
                {/* Date Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    📅 Escolha o dia
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {schedulingSlots.slice(0, 6).map((slot) => (
                      <button
                        key={slot.date}
                        onClick={() => {
                          setSelectedScheduleDate(slot.date);
                          setSelectedScheduleTime(null);
                        }}
                        className={`p-2 rounded-lg border-2 transition-all duration-200 text-center ${
                          selectedScheduleDate === slot.date
                            ? 'border-amber-500 bg-amber-50 shadow-md'
                            : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'
                        }`}
                        data-testid={`schedule-date-${slot.date}`}
                      >
                        <div className="font-semibold text-sm text-gray-800">
                          {slot.is_today ? 'Hoje' : slot.is_tomorrow ? 'Amanhã' : slot.day_name?.slice(0,3)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {slot.date_formatted?.split('/').slice(0,2).join('/')}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time Selection */}
                {selectedScheduleDate && selectedSlot && (
                  <div className="mb-4 animate-fade-in">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ⏰ Escolha o horário
                    </label>
                    <div className="grid grid-cols-4 gap-1.5 max-h-28 overflow-y-auto">
                      {selectedSlot.time_slots.map((time) => (
                        <button
                          key={time}
                          onClick={() => setSelectedScheduleTime(time)}
                          className={`py-2 px-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                            selectedScheduleTime === time
                              ? 'border-amber-500 bg-amber-500 text-white shadow-md'
                              : 'border-gray-200 text-gray-700 hover:border-amber-300 hover:bg-gray-50'
                          }`}
                          data-testid={`schedule-time-${time}`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Summary - Compacto */}
                {selectedScheduleDate && selectedScheduleTime && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 animate-fade-in">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-green-800">
                          <span className="font-semibold">Agendado:</span>{' '}
                          {selectedSlot?.is_today ? 'Hoje' : selectedSlot?.is_tomorrow ? 'Amanhã' : selectedSlot?.day_name} às <span className="font-bold">{selectedScheduleTime}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer Actions - Sticky */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white rounded-b-2xl safe-area-bottom">
            <button
              onClick={handleConfirmScheduling}
              disabled={!selectedScheduleDate || !selectedScheduleTime}
              className={`w-full py-3.5 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                selectedScheduleDate && selectedScheduleTime
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg hover:shadow-xl active:scale-[0.98]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="confirm-scheduling-btn"
            >
              <Clock className="w-5 h-5" />
              {selectedScheduleDate && selectedScheduleTime ? 'Confirmar e Continuar' : 'Selecione data e horário'}
            </button>
            <button
              onClick={handleCancelScheduling}
              className="w-full py-2.5 mt-2 rounded-xl text-gray-500 text-sm font-medium hover:bg-gray-100 transition-colors"
              data-testid="cancel-scheduling-btn"
            >
              Voltar ao cardápio
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Minimum Order Modal Component - Compacto e Discreto
  const MinimumOrderModal = () => {
    if (!showMinOrderModal) return null;

    const minimumOrder = restaurant?.minimum_order || 0;
    const cartTotal = getCartTotal();
    const remaining = minimumOrder - cartTotal;

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
        {/* Backdrop suave */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-40"
          onClick={() => setShowMinOrderModal(false)}
        />
        
        {/* Modal Compacto */}
        <div className="relative bg-white rounded-2xl w-full max-w-sm shadow-xl animate-scale-in overflow-hidden">
          {/* Barra superior colorida */}
          <div className="h-1.5 bg-gradient-to-r from-amber-400 to-orange-400"></div>
          
          {/* Conteúdo */}
          <div className="p-5">
            {/* Header inline */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800">Quase lá!</h3>
                <p className="text-xs text-gray-500">Pedido mínimo para delivery</p>
              </div>
            </div>
            
            {/* Valores em linha */}
            <div className="bg-gray-50 rounded-xl p-3 mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500">Seu carrinho</span>
                <span className="font-semibold">R$ {cartTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500">Mínimo</span>
                <span className="font-semibold text-amber-600">R$ {minimumOrder.toFixed(2)}</span>
              </div>
              <div className="border-t border-gray-200 pt-2 flex justify-between">
                <span className="text-gray-700 font-medium text-sm">Falta</span>
                <span className="font-bold text-orange-500">R$ {remaining.toFixed(2)}</span>
              </div>
            </div>
            
            {/* Botões lado a lado */}
            <div className="flex space-x-3">
              <button
                onClick={() => setShowMinOrderModal(false)}
                className="flex-1 py-2.5 px-4 border border-gray-200 rounded-xl text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Voltar
              </button>
              <button
                onClick={() => {
                  setShowMinOrderModal(false);
                  setCurrentScreen('home');
                }}
                className="flex-1 py-2.5 px-4 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary-hover transition-colors flex items-center justify-center space-x-1"
              >
                <Plus className="w-4 h-4" />
                <span>Adicionar</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Order Details Modal Component
  const OrderDetailsModal = () => {
    if (!selectedOrderDetails) return null;

    const order = selectedOrderDetails;

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatTime = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    const getStatusColor = (status) => {
      const colors = {
        'preparando': 'bg-primary',
        'pronto': 'bg-green-500',
        'entregue': 'bg-gray-500',
        'cancelado': 'bg-red-500'
      };
      return colors[status] || 'bg-gray-500';
    };
    
    const getStatusLabel = (status, deliveryType = 'delivery') => {
      // Para status 'entrega', diferenciar entre delivery e retirada
      if (status === 'entrega') {
        if (deliveryType === 'retirada' || deliveryType === 'pickup') {
          return 'Pronto p/ Retirada';
        }
        return 'Saiu p/ Entrega';
      }
      
      // Para status 'pronto', diferenciar entre delivery e retirada
      if (status === 'pronto' || status === 'entregue') {
        if (deliveryType === 'retirada' || deliveryType === 'pickup') {
          return 'Retirado';
        }
        return 'Entregue';
      }
      
      const labels = {
        'pendente': 'Pendente',
        'em_producao': 'Em Produção',
        'preparando': 'Preparando',
        'cancelado': 'Cancelado'
      };
      return labels[status] || status;
    };

    const getDeliveryTypeLabel = (type) => {
      const labels = {
        'delivery': '🚚 Entrega',
        'retirada': '🏪 Retirada',
        'mesa': '🪑 Mesa'
      };
      return labels[type] || type;
    };

    const getPaymentMethodLabel = (method) => {
      const labels = {
        'dinheiro': '💵 Dinheiro',
        'pix': '📱 PIX',
        'pix_mp': '🚀 PIX Automático',
        'cartao': '💳 Cartão',
        'cartao_credito': '💳 Cartão de Crédito',
        'cartao_debito': '💳 Cartão de Débito',
        'vale_refeicao': '🍽️ Vale Refeição',
        'vale_alimentacao': '🛒 Vale Alimentação'
      };
      return labels[method] || method;
    };

    return (
      <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
          onClick={() => setSelectedOrderDetails(null)}
        />
        
        {/* Modal Content */}
        <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-t-3xl sm:rounded-t-2xl z-10">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-3 backdrop-blur-sm">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1">
                Pedido #{order.order_number}
              </h3>
              <p className="text-white text-opacity-90 text-sm">
                {formatDate(order.created_at)} às {formatTime(order.created_at)}
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* Status Badge */}
            <div className="flex justify-center">
              <span className={`px-4 py-2 rounded-full text-white text-sm font-medium ${getStatusColor(order.status)}`}>
                {getStatusLabel(order.status, order.delivery_type)}
              </span>
            </div>

            {/* Items Section */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <ShoppingCart size={18} />
                Itens do Pedido
              </h4>
              <div className="space-y-3">
                {order.items && order.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">
                        {item.quantity}x {item.product_name}
                      </p>
                      {item.additionals && item.additionals.length > 0 && (
                        <div className="mt-1 ml-4 space-y-1">
                          {item.additionals.map((add, addIdx) => (
                            <p key={addIdx} className="text-xs text-gray-600">
                              + {add.quantity}x {add.name} (R$ {add.price?.toFixed(2)})
                            </p>
                          ))}
                        </div>
                      )}
                      {item.notes && (
                        <p className="text-xs text-gray-500 mt-1 italic">
                          Obs: {item.notes}
                        </p>
                      )}
                    </div>
                    <span className="text-gray-700 font-medium ml-2">
                      R$ {((item.price || 0) * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery Info */}
            {order.delivery_type && (
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Truck size={18} />
                  Informações de Entrega
                </h4>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-700">
                    <span className="font-medium">Tipo:</span> {getDeliveryTypeLabel(order.delivery_type)}
                  </p>
                  {order.address && (
                    <>
                      <p className="text-gray-700">
                        <span className="font-medium">Endereço:</span> {order.address.street}, {order.address.number}
                      </p>
                      {order.address.complement && (
                        <p className="text-gray-700">
                          <span className="font-medium">Complemento:</span> {order.address.complement}
                        </p>
                      )}
                      <p className="text-gray-700">
                        <span className="font-medium">Bairro:</span> {order.address.neighborhood}
                      </p>
                      {order.address.city && (
                        <p className="text-gray-700">
                          <span className="font-medium">Cidade:</span> {order.address.city}
                        </p>
                      )}
                    </>
                  )}
                  {order.table_number && (
                    <p className="text-gray-700">
                      <span className="font-medium">Mesa:</span> {order.table_number}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Payment Info */}
            {order.payment_method && (
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <DollarSign size={18} />
                  Pagamento
                </h4>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-700">
                    <span className="font-medium">Forma:</span> {getPaymentMethodLabel(order.payment_method)}
                  </p>
                  {order.change_for && (
                    <p className="text-gray-700">
                      <span className="font-medium">Troco para:</span> R$ {order.change_for.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Customer Info */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold text-gray-800 mb-3">Cliente</h4>
              <div className="space-y-2 text-sm">
                <p className="text-gray-700">
                  <span className="font-medium">Nome:</span> {order.customer_name || 'Não informado'}
                </p>
                <p className="text-gray-700">
                  <span className="font-medium">Telefone:</span> {order.customer_phone || 'Não informado'}
                </p>
              </div>
            </div>

            {/* Total Section */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Total do Pedido</span>
                <span className="text-2xl font-bold">
                  R$ {order.total?.toFixed(2) || '0.00'}
                </span>
              </div>
              {order.delivery_fee > 0 && (
                <p className="text-sm text-white text-opacity-90 mt-2">
                  (Inclui taxa de entrega: R$ {order.delivery_fee.toFixed(2)})
                </p>
              )}
              {order.cashback_used > 0 && (
                <p className="text-sm text-white text-opacity-90 mt-1">
                  💰 Cashback usado: R$ {order.cashback_used.toFixed(2)}
                </p>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={() => setSelectedOrderDetails(null)}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 px-6 rounded-xl font-semibold transition-all duration-200 active:scale-95"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Promos/Cashback Screen Component  
  const PromosScreen = () => {
    const [phoneNumber, setPhoneNumber] = useState(customerPhone);
    const [loadingCashback, setLoadingCashback] = useState(false);
    const [expandedFAQ, setExpandedFAQ] = useState(null);
    const promosPhoneRef = useRef(null);

    // Auto-load cashback when phone is available
    React.useEffect(() => {
      if (customerPhone && !cashbackData && restaurant) {
        loadCashbackData(customerPhone);
      }
    }, [customerPhone, restaurant]); // Removed cashbackData to avoid loop

    const handleCheckCashback = async () => {
      const phone = promosPhoneRef.current?.value || phoneNumber;
      if (!phone || phone.length < 10) {
        alert('Por favor, insira um telefone válido');
        return;
      }

      setLoadingCashback(true);
      await loadCashbackData(phone);
      setLoadingCashback(false);
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR');
    };

    const faqItems = [
      {
        question: 'Como funciona o cashback?',
        answer: `A cada compra você acumula ${cashbackData?.settings?.percentage || 5}% do valor em créditos que podem ser usados em compras futuras.`
      },
      {
        question: 'Quando posso usar meu cashback?',
        answer: 'Você pode usar seu cashback em qualquer pedido, no momento do checkout.'
      },
      {
        question: 'O cashback expira?',
        answer: `Sim, o cashback expira após ${cashbackData?.settings?.expiry_days || 30} dias.`
      },
      {
        question: 'Como verifico meu saldo?',
        answer: 'Seu saldo é carregado automaticamente quando você faz pedidos.'
      }
    ];

    return (
      <div 
        className="pt-16 pb-20 h-screen overflow-y-auto bg-gray-50"
        data-testid="promos-screen"
      >
        {/* Cashback Tab */}
        <div className="bg-white border-b">
          <div className="px-4 py-2">
            <button className="text-primary font-medium text-sm border-b-2 border-primary pb-2">
              Cashback
            </button>
          </div>
        </div>

        {/* Phone Input Section - Only show if no phone saved */}
        {!customerPhone && !cashbackData && (
          <div className="p-4 bg-white border-b">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Digite seu telefone para verificar seu cashback
            </label>
            <div className="flex gap-2">
              <input
                ref={promosPhoneRef}
                type="tel"
                defaultValue={phoneNumber}
                onBlur={(e) => setPhoneNumber(e.target.value)}
                placeholder="(00) 00000-0000"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                data-testid="cashback-phone-input"
              />
              <button
                onClick={handleCheckCashback}
                disabled={loadingCashback}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50"
                data-testid="check-cashback-button"
              >
                {loadingCashback ? 'Carregando...' : 'Verificar'}
              </button>
            </div>
          </div>
        )}

        {/* Cashback Balance Section */}
        {cashbackData && cashbackData.enabled && (
          <div className="p-4 space-y-4">
            {/* Available Balance */}
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="text-primary" size={20} />
                <span className="text-sm text-gray-600">Saldo disponível</span>
              </div>
              <div className="text-3xl font-bold text-blue-600" data-testid="available-balance">
                R$ {cashbackData.available_balance.toFixed(2)}
              </div>
            </div>

            {/* Expired Balance */}
            {cashbackData.expired_balance > 0 && (
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="text-gray-500" size={16} />
                  <span className="text-sm text-gray-600">
                    R$ {cashbackData.expired_balance.toFixed(2)} expirado em {formatDate(cashbackData.transactions?.find(t => t.expired)?.expires_at)}
                  </span>
                </div>
              </div>
            )}

            {/* Call to Action */}
            <div className="bg-white rounded-xl p-6 text-center border border-gray-200">
              <p className="text-blue-600 font-medium text-lg">
                Compre e ganhe cashback
              </p>
            </div>

            {/* Rules Section */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">i</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Regra</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Economize mais a cada compra. Todos os pedidos acumulam créditos que você pode 
                    resgatar na próxima compra.
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                onClick={() => setExpandedFAQ(expandedFAQ === 'faq' ? null : 'faq')}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                data-testid="faq-toggle"
              >
                <span className="font-medium text-gray-800">Perguntas frequentes</span>
                <ChevronDown 
                  className={`text-gray-400 transition-transform ${expandedFAQ === 'faq' ? 'rotate-180' : ''}`} 
                  size={20} 
                />
              </button>
              
              {expandedFAQ === 'faq' && (
                <div className="px-6 pb-4 space-y-4 border-t border-gray-100" data-testid="faq-content">
                  {faqItems.map((item, index) => (
                    <div key={index} className="space-y-2">
                      <h4 className="font-medium text-gray-800 text-sm">{item.question}</h4>
                      <p className="text-sm text-gray-600">{item.answer}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Change Phone Button */}
            <button
              onClick={() => {
                setCashbackData(null);
                setPhoneNumber('');
              }}
              className="w-full py-3 text-primary font-medium text-sm"
              data-testid="change-phone-button"
            >
              Verificar outro telefone
            </button>
          </div>
        )}

        {/* Cashback Not Enabled Message */}
        {cashbackData && !cashbackData.enabled && (
          <div className="p-4">
            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200 text-center">
              <p className="text-yellow-800">
                O programa de cashback não está ativo no momento.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };


  // Orders History Screen Component
  const OrdersHistoryScreen = () => {
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };
    
    const formatTime = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };
    
    const getStatusColor = (status) => {
      const colors = {
        'preparando': 'bg-primary',
        'pronto': 'bg-green-500',
        'entregue': 'bg-gray-500',
        'cancelado': 'bg-red-500'
      };
      return colors[status] || 'bg-gray-500';
    };
    
    const getStatusLabel = (status, deliveryType = 'delivery') => {
      // Para status 'entrega', diferenciar entre delivery e retirada
      if (status === 'entrega') {
        if (deliveryType === 'retirada' || deliveryType === 'pickup') {
          return 'Pronto p/ Retirada';
        }
        return 'Saiu p/ Entrega';
      }
      
      // Para status 'pronto', diferenciar entre delivery e retirada
      if (status === 'pronto' || status === 'entregue') {
        if (deliveryType === 'retirada' || deliveryType === 'pickup') {
          return 'Retirado';
        }
        return 'Entregue';
      }
      
      const labels = {
        'pendente': 'Pendente',
        'em_producao': 'Em Produção',
        'preparando': 'Preparando',
        'cancelado': 'Cancelado'
      };
      return labels[status] || status;
    };

    return (
      <div 
        className="pt-16 pb-20 h-screen overflow-y-auto bg-gray-50"
        data-testid="orders-history-screen"
      >
        <div className="p-4 space-y-4">
          {/* Header */}
          <div className="text-center mb-2">
            <h2 className="text-xl font-bold text-gray-800">Meus pedidos</h2>
          </div>

          {!customerPhone || !orderHistory || orderHistory.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto text-gray-400 mb-4" size={64} />
              <p className="text-gray-600 mb-2">Nenhum pedido encontrado</p>
              <p className="text-sm text-gray-500">
                {!customerPhone 
                  ? 'Faça seu primeiro pedido para ver o histórico aqui'
                  : 'Você ainda não fez nenhum pedido'}
              </p>
            </div>
          ) : (
            orderHistory.map((order) => (
              <div 
                key={order.id} 
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                data-testid={`order-card-${order.order_number}`}
              >
                {/* Order Header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-bold text-gray-800">Pedido #{order.order_number}</h3>
                      <p className="text-sm text-gray-500">
                        Em {formatDate(order.created_at)} às {formatTime(order.created_at)}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-white text-xs font-medium ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status, order.delivery_type)}
                    </span>
                  </div>
                  
                  {/* Items List */}
                  <div className="mt-3 space-y-1">
                    {order.items && order.items.map((item, idx) => (
                      <div key={idx} className="text-sm text-gray-600">
                        {item.quantity}x {item.product_name}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Footer */}
                <div className="p-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-gray-800">
                      R$ {order.total?.toFixed(2) || '0.00'}
                    </span>
                    <div className="flex gap-2">
                      <button 
                        className="px-4 py-2 border border-primary text-primary rounded-lg text-sm font-medium hover:bg-primary-light transition-colors"
                        onClick={() => setSelectedOrderDetails(order)}
                        data-testid={`order-details-btn-${order.order_number}`}
                      >
                        Detalhes do pedido
                      </button>
                      <button 
                        className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-hover transition-colors"
                        onClick={() => handleReorder(order)}
                        data-testid={`reorder-btn-${order.order_number}`}
                      >
                        Repetir pedido
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // ✅ Componente: Tela de Confirmação de Pedido
  const OrderConfirmationScreen = () => {
    if (!showOrderConfirmation || !confirmationOrderData) return null;
    
    const { orderNumber, total, whatsappMessage, restaurantPhone, restaurantName } = confirmationOrderData;
    
    const handleSendWhatsApp = () => {
      if (!restaurantPhone) {
        toast.error('O restaurante ainda não configurou o WhatsApp');
        return;
      }
      
      // Limpar número e criar URL
      const cleanPhone = restaurantPhone.replace(/\D/g, '');
      const encodedMessage = encodeURIComponent(whatsappMessage);
      const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
      
      // Usar location.href - funciona igual em iOS e Android
      window.location.href = whatsappUrl;
    };
    
    const handleClose = () => {
      setShowOrderConfirmation(false);
      setConfirmationOrderData(null);
      setIsSubmittingOrder(false);
    };
    
    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden animate-slide-up">
          {/* Conteúdo principal */}
          <div className="p-8 text-center">
            {/* Ícone de sucesso animado */}
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <svg 
                  className="w-10 h-10 text-green-500" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={3} 
                    d="M5 13l4 4L19 7" 
                  />
                </svg>
              </div>
            </div>
            
            {/* Mensagem de sucesso */}
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Pedido Realizado!
            </h2>
            
            <p className="text-gray-500 mb-1">
              Pedido <span className="font-semibold text-gray-700">#{orderNumber}</span>
            </p>
            
            <p className="text-2xl font-bold text-green-600 mb-4">
              R$ {total.toFixed(2)}
            </p>
            
            {/* Agradecimento */}
            <p className="text-gray-600 text-lg mb-6">
              Muito obrigado <span className="text-red-500">♥️</span>
            </p>
            
            {/* Botão WhatsApp */}
            {restaurantPhone ? (
              <button
                onClick={handleSendWhatsApp}
                className="w-full py-4 px-6 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-2xl transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Enviar pelo WhatsApp
              </button>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
                <p className="text-yellow-700 text-sm">
                  ⚠️ Este restaurante ainda não configurou o WhatsApp.
                </p>
                <p className="text-yellow-600 text-xs mt-1">
                  Seu pedido foi registrado! Aguarde o contato da loja.
                </p>
              </div>
            )}
          </div>
          
          {/* Botão fechar */}
          <div className="px-8 pb-6">
            <button
              onClick={handleClose}
              className="w-full py-3 text-gray-500 hover:text-gray-700 font-medium transition"
            >
              Voltar ao cardápio
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };



  return (
    <div className={`min-h-screen bg-gray-50 ${currentScreen === 'cart' || currentScreen === 'checkout' ? 'max-h-screen overflow-hidden' : 'max-h-screen overflow-hidden'}`}>
      {/* Search Modal */}
      <SearchModal />
      
      <style jsx>{`
        @keyframes slow-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .7; }
        }
        .animate-slow-pulse {
          animation: slow-pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        
        /* Continuous pulse animation for cart icon */
        @keyframes cart-pulse {
          0%, 100% { 
            transform: scale(1);
          }
          50% { 
            transform: scale(1.1);
          }
        }
        
        /* Bounce once animation for success checkmark */
        @keyframes bounce-once {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        .animate-bounce-once {
          animation: bounce-once 0.6s ease-out forwards;
        }
        
        /* Single bounce animation when modal opens */
        @keyframes cart-bounce {
          0% {
            transform: translateY(0) scale(1);
          }
          25% {
            transform: translateY(-20px) scale(1.05);
          }
          50% {
            transform: translateY(0) scale(1);
          }
          65% {
            transform: translateY(-10px) scale(1.02);
          }
          80% {
            transform: translateY(0) scale(1);
          }
          90% {
            transform: translateY(-5px) scale(1.01);
          }
          100% {
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-cart-pulse {
          animation: cart-pulse 1.5s ease-in-out infinite;
        }
        
        .animate-cart-bounce-once {
          animation: cart-bounce 0.8s ease-out;
        }
        
        /* Hide scrollbar for categories */
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        /* Ensure scrolling works on mobile */
        body {
          overflow: hidden;
          position: fixed;
          width: 100%;
          height: 100%;
        }
        
        /* Custom scrollbar for cart */
        .cart-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .cart-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .cart-scroll::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }
        .cart-scroll::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
      
      <Header />
      
      {currentScreen === 'home' && <HomeScreen />}
      {currentScreen === 'product' && selectedProduct && <ProductScreen />}
      {currentScreen === 'cart' && <CartScreen />}
      {currentScreen === 'checkout' && <CheckoutScreen />}
      {currentScreen === 'promos' && <PromosScreen />}
      {currentScreen === 'orders' && <OrdersHistoryScreen />}
      
      <BottomNav />
      
      {/* Store Profile Modal */}
      {showStoreProfile && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:w-[500px] sm:rounded-t-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center">
              <button 
                onClick={() => setShowStoreProfile(false)}
                className="p-2 -ml-2 hover:bg-gray-100 rounded-full"
              >
                <ArrowLeft size={24} className="text-gray-700" />
              </button>
              <h2 className="text-lg font-semibold text-gray-900 ml-3">Sobre o estabelecimento</h2>
            </div>

            {/* Store Name */}
            <div className="px-4 py-6 border-b border-gray-200">
              <h1 className="text-3xl font-bold text-gray-900">{restaurant?.name}</h1>
            </div>

            {/* Operating Hours */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">Horário de atendimento</h3>
            </div>
            <div className="px-4 py-4 border-b border-gray-200">
              {restaurant?.schedule?.auto_open_close ? (
                <div className="space-y-2">
                  {Object.entries({
                    sunday: 'Domingo',
                    monday: 'Segunda-feira',
                    tuesday: 'Terça-feira',
                    wednesday: 'Quarta-feira',
                    thursday: 'Quinta-feira',
                    friday: 'Sexta-feira',
                    saturday: 'Sábado'
                  }).map(([day, label]) => {
                    const daySchedule = restaurant.schedule.schedule?.[day];
                    return (
                      <div key={day} className="flex justify-between text-sm">
                        <span className="text-gray-700">{label}</span>
                        <span className="text-gray-900 font-medium">
                          {daySchedule?.enabled 
                            ? `${daySchedule.open} às ${daySchedule.close}` 
                            : 'Fechado'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-600">Horários não configurados</p>
              )}
            </div>

            {/* Delivery Information */}
            {(restaurant?.delivery_time || restaurant?.minimum_order) && (
              <>
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700">Informações de entrega</h3>
                </div>
                <div className="px-4 py-4 border-b border-gray-200">
                  <div className="space-y-3">
                    {restaurant?.delivery_time && (
                      <div className="flex items-center text-sm text-gray-900">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <span className="font-medium">Tempo de entrega:</span>
                          <span className="ml-2">{restaurant.delivery_time}</span>
                        </div>
                      </div>
                    )}
                    {restaurant?.minimum_order > 0 && (
                      <div className="flex items-center text-sm text-gray-900">
                        <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mr-3">
                          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div>
                          <span className="font-medium">Pedido mínimo:</span>
                          <span className="ml-2">R$ {restaurant.minimum_order?.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Payment Methods */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">Formas de pagamento</h3>
            </div>
            <div className="px-4 py-4 border-b border-gray-200">
              {restaurant?.payment_methods ? (
                <div className="space-y-3">
                  {restaurant.payment_methods.pix && (
                    <div className="flex items-center text-sm text-gray-900">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-teal-600 font-bold text-xs">PIX</span>
                      </div>

                      <span>Pix</span>
                    </div>
                  )}
                  {restaurant.payment_methods.cartao && (
                    <div className="flex items-center text-sm text-gray-900">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                          <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <span>Cartão de Crédito/Débito</span>
                    </div>
                  )}
                  {restaurant.payment_methods.dinheiro && (
                    <div className="flex items-center text-sm text-gray-900">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-green-600 font-bold text-lg">$</span>
                      </div>
                      <span>Dinheiro</span>
                    </div>
                  )}
                  {restaurant.payment_methods.vale_refeicao && (
                    <div className="flex items-center text-sm text-gray-900">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-orange-600 font-bold text-xs">VR</span>
                      </div>
                      <span>Vale Refeição</span>
                    </div>
                  )}
                  {restaurant.payment_methods.vale_alimentacao && (
                    <div className="flex items-center text-sm text-gray-900">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-purple-600 font-bold text-xs">VA</span>
                      </div>
                      <span>Vale Alimentação</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600">Informações de pagamento não configuradas</p>
              )}
            </div>

            {/* Contact Information */}
            {restaurant?.phone && (
              <>
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700">Contato</h3>
                </div>
                <div className="px-4 py-4 border-b border-gray-200">
                  <a 
                    href={`https://wa.me/${restaurant.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between text-sm text-gray-900 hover:bg-gray-50 -mx-4 px-4 py-3 transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </div>
                      <span>{restaurant.phone}</span>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              </>
            )}

            {/* Close Button */}
            <div className="px-4 py-6">
              <button
                onClick={() => setShowStoreProfile(false)}
                className="w-full bg-primary hover:bg-primary-hover text-white py-3 rounded-lg font-medium transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
      
      <CartConfirmationModal />
      <ClosedStoreModal />
      <SchedulingModal />
      <MinimumOrderModal />
      <OrderDetailsModal />
      <OrderConfirmationScreen />
      
      {/* PIX Checkout Modal */}
      <PixCheckoutModal 
        isOpen={showPixCheckout}
        onClose={() => {
          setShowPixCheckout(false);
          setPixOrderData(null);
        }}
        orderData={pixOrderData}
        subdomain={subdomain}
        onSuccess={(confirmedOrder) => {
          // Salvar dados do pedido confirmado para a tela de sucesso
          setSuccessOrderData(confirmedOrder || pixOrderData);
          
          // Limpar carrinho e estados
          setCart([]);
          setUseCashback(false);
          setCashbackAmount(0);
          setShowPixCheckout(false);
          
          // Mostrar tela de sucesso ao invés de voltar para home
          setShowPaymentSuccess(true);
          
          // Reload cashback data if customer phone is available
          if (pixOrderData?.customer_phone) {
            loadCashbackData(pixOrderData.customer_phone);
          }
        }}
      />
      
      {/* 🎉 Payment Success Modal */}
      {showPaymentSuccess && successOrderData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
            {/* Success Header */}
            <div className="bg-gradient-to-br from-green-400 to-green-600 p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-full flex items-center justify-center shadow-lg animate-bounce-once">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Pagamento Confirmado!</h2>
              <p className="text-green-100 text-sm">Seu pedido foi recebido com sucesso</p>
            </div>
            
            {/* Order Summary */}
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-600 text-sm">Pedido</span>
                  <span className="font-bold text-gray-800">#{successOrderData.order_number || '---'}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-600 text-sm">Total Pago</span>
                  <span className="font-bold text-green-600 text-lg">
                    R$ {(successOrderData.total || 0).toFixed(2)}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <p className="text-xs text-gray-500 text-center">
                    📱 PIX aprovado via Mercado Pago
                  </p>
                </div>
              </div>
              
              {/* WhatsApp Button */}
              <button
                onClick={() => {
                  // Construir mensagem resumida para WhatsApp
                  const order = successOrderData;
                  let message = `✅ *PAGAMENTO CONFIRMADO*\n\n`;
                  message += `📝 *Pedido #${order.order_number || '---'}*\n`;
                  message += `👤 *Cliente:* ${order.customer_name || 'Cliente'}\n`;
                  message += `📞 *Telefone:* ${order.customer_phone || '-'}\n\n`;
                  
                  message += `🛒 *Itens:*\n`;
                  (order.items || []).forEach(item => {
                    message += `• ${item.quantity}x ${item.product_name || item.name}\n`;
                  });
                  
                  message += `\n💰 *Total:* R$ ${(order.total || 0).toFixed(2)}`;
                  message += `\n💳 *Pagamento:* PIX (Aprovado ✓)`;
                  
                  if (order.delivery_type === 'delivery' && order.address) {
                    const addr = order.address;
                    message += `\n\n📍 *Entregar em:*\n`;
                    message += `${addr.street || ''}, ${addr.number || ''}`;
                    if (addr.complement) message += ` - ${addr.complement}`;
                    if (addr.neighborhood) message += `\n${addr.neighborhood}`;
                  } else if (order.delivery_type === 'pickup') {
                    message += `\n\n🏪 *Retirada no local*`;
                  }
                  
                  message += `\n\n---\n✨ Pedido pago pelo cardápio digital`;
                  
                  // Abrir WhatsApp
                  const phone = restaurant?.phone;
                  if (phone) {
                    const whatsappUrl = `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
                    window.open(whatsappUrl, '_blank');
                  } else {
                    showToast('WhatsApp do estabelecimento não configurado', 'warning');
                  }
                }}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-4 px-6 rounded-xl flex items-center justify-center gap-3 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
                data-testid="whatsapp-success-btn"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Enviar Pedido no WhatsApp
              </button>
              
              {/* Secondary Button - Back to Menu */}
              <button
                onClick={() => {
                  setShowPaymentSuccess(false);
                  setSuccessOrderData(null);
                  setPixOrderData(null);
                  setCurrentScreen('home');
                }}
                className="w-full mt-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-colors"
                data-testid="back-to-menu-btn"
              >
                Voltar ao Cardápio
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast Notification */}
      {toastMessage && (
        <div 
          className={`fixed bottom-20 left-4 right-4 z-50 mx-auto max-w-md animate-slide-up`}
          data-testid="toast-message"
        >
          <div className={`rounded-lg shadow-lg p-4 flex items-center gap-3 ${
            toastMessage.type === 'success' ? 'bg-green-500 text-white' :
            toastMessage.type === 'error' ? 'bg-red-500 text-white' :
            toastMessage.type === 'warning' ? 'bg-orange-500 text-white' :
            'bg-primary text-white'
          }`}>
            <div className="flex-1">
              <p className="text-sm font-medium">{toastMessage.text}</p>
            </div>
            <button
              onClick={() => setToastMessage(null)}
              className="flex-shrink-0 text-white hover:opacity-80 transition-opacity"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicMenu;
